import { solarStringColourKeys } from '@payaca/helpers/solarStringColours';
import { extractErrorMessage } from '@/api/extractErrorMessage';
import { useCalculateSolarDesignPerformance } from '@/api/mutations/project/useCalculateSolarDesignPerformance';
import { solarDesignDraftStateSchema } from '@payaca/helpers/solarDesignDraft';
import {
  solarElectricalEquipmentCost,
  solarElectricalEquipmentQuantities,
} from '@payaca/helpers/solarElectricalEquipmentCost';
import { useSolarElectricalPrices } from '@/api/queries/appliances/useSolarElectricalPrices';
import {
  currentSolarWeatherSnapshot,
  solarWeatherUnavailableMessage,
  type SolarWeatherGeneration,
} from '@payaca/helpers/solarWeatherGeneration';
import { physicalRoofPlane } from '@payaca/helpers/solarMounting';
import {
  solarDesignRegistersToAnalysedImagery,
  solarRepackBlockedReason,
} from '@payaca/helpers/solarPackLayout';
import { roofSurfaceAreaMeters2 } from '@payaca/helpers/solarDesignerUnits';
import { siteDesignMinTemperatureC } from '@payaca/helpers/solarSiteDesignTemperature';
import useCreateEstimate from '@/api/mutations/estimates/useCreateEstimate';
import { solarRoofRecoveryMessage } from '@/helpers/solarRoofRecovery';
import { useDeleteSolarDesign } from '@/api/mutations/project/useDeleteSolarDesign';
import { useSaveSolarDesignDraft } from '@/api/mutations/project/useSaveSolarDesignDraft';
import { type SolarGeometryLocation } from '@/api/mutations/project/useGenerateSolarGeometry';
import {
  type SolarDesignDraftState,
  type SolarInverterSelection,
} from '@payaca/helpers/solarDesignDraft';
import { installedBatteryCapacityKwh } from '@payaca/helpers/solarInstalledEquipment';
import { eessAllocatedCapacityKwh } from '@payaca/helpers/solarEessBenefits';
import { parseSolarEessSettings } from '@payaca/helpers/solarEessSettings';
import {
  solarGenerationAvailability,
  generationStatusHasResult,
  faceGenerationStatus,
} from '@payaca/helpers/solarGenerationAvailability';
import {
  parseSolarPlacementRules,
  recordRoofBearingRules,
  roofBearingRulesNeedRepack,
  type SolarPlacementRules,
} from '@payaca/helpers/solarPlacementRules';
import { estimateSelfConsumption } from '@payaca/helpers/solarSelfConsumption';
import {
  getKkValue,
  orientationFromSouth,
  resolvePostcodeToZone,
} from '@payaca/helpers/solarMcsLookup';
import { useDesignHistory } from './solarDesigner/useDesignHistory';
import {
  restoreSolarDesignHistory,
  type SolarDesignRestoreReason,
} from '@payaca/helpers/solarDesignHistoryRestore';
import { appliedArraySnap } from './solarDesigner/appliedArraySnap';
import { useAutoSaveDesign } from './solarDesigner/useAutoSaveDesign';
import { useSolarDesignerInverters } from '@/api/queries/project/useSolarDesignerInverters';
import {
  effectiveSolarApplianceId,
  savedSolarPanelSelectionMatches,
  useAutomaticSolarEquipment,
} from './solarDesigner/useAutomaticSolarEquipment';
import { resolveQuotedSolarModules } from '@payaca/helpers/solarQuotedModules';
import useGetBuildingFootprints from '@/api/queries/project/useGetBuildingFootprints';
import { ProjectSolarPotential } from '@/api/queries/project/useGetProjectSolarPotential';
import useGetTitleParcelAt from '@/api/queries/project/useGetTitleParcelAt';
import { useAccount } from '@/api/rest/users/useAccount';
import { AccountRegions } from '@payaca/types/accountTypes';
import { Alert, Button, H4, Modal, Text, Tooltip } from '@payaca/ambrosia';
import DesignerWorkspace from './solarDesigner/DesignerWorkspace';
import { selectedLayoutStatus } from './solarDesigner/selectedLayoutStatus';
import DesignerActions from './solarDesigner/DesignerActions';
import DesignerSummary from './solarDesigner/DesignerSummary';
import useStableFaceOrder from './solarDesigner/useStableFaceOrder';
import SuggestedLayoutControls from './solarDesigner/SuggestedLayoutControls';
import { analysisGenerationEstimate } from './solarDesigner/analysisGenerationEstimate';
import {
  SOLAR_STRING_PALETTE,
  SolarRoofView,
} from '@payaca/components/solarRoofView/SolarRoofView';
import UntitledIcon from '@payaca/untitled-icons';
import { formatDate } from 'date-fns';
import {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import ContextPanel from './solarDesigner/ContextPanel';
import MapLayersButton from './solarDesigner/MapLayersButton';
import UsNetMeteringScenario, {
  derivedUsNetMeteringPolicy,
} from './solarDesigner/UsNetMeteringScenario';
import { solarMonthlySourceForDesign } from '@payaca/helpers/solarMonthlySource';
import {
  calculateSolarUsNetMeteringPolicy,
  solarUsNetMeteringWeatherProvenance,
  type SolarUsNetMeteringPolicy,
} from '@payaca/helpers/solarUsNetMeteringPolicy';
import { selfConsumptionFraction } from '@payaca/helpers/solarSavings';
import { deriveRoofViewProps } from './solarDesigner/deriveRoofViewProps';
import { mcsPerPanelByWorkingFace } from './solarDesigner/mcsYieldByFace';
import { parcelsMismatch } from './solarDesigner/parcelMismatch';
import ParcelMismatchHint from './solarDesigner/ParcelMismatchHint';
import HouseholdElectricitySection, {
  type HouseholdElectricity,
} from './solarDesigner/HouseholdElectricitySection';
import ProductsPanel from './solarDesigner/ProductsPanel';
import { ElectricalAllocationPanel } from './solarDesigner/ElectricalAllocationPanel';
import { applySolarElectricalDesignChange } from '@payaca/helpers/solarElectricalChange';
import {
  allocatedSolarElectrical,
  type SolarElectricalTopology,
} from '@payaca/helpers/solarElectricalTopology';
import {
  upgradeSolarDesignElectrical,
  restoreSolarPanelIdentities,
} from '@payaca/helpers/solarDesignElectricalUpgrade';
import { solarElectricalLayout } from '@payaca/helpers/solarElectricalLayout';
import ToolRail from './solarDesigner/ToolRail';
import ElectricalTab from './solarDesigner/ElectricalTab';
import CanvasEditingBar, {
  type CanvasMode,
} from './solarDesigner/CanvasEditingBar';
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import projectKeys from '@/api/queries/project/keyFactory';
import { solarDesignerStateKey } from '@/api/queries/project/useSolarDesignerState';
import {
  moveSelectedPanels,
  panelSelectionIndexes,
  selectPanel,
  type PanelSelection,
} from './solarDesigner/panelSelection';
import {
  DuplicateSolarDesignModal,
  EditSolarDesignDetailsModal,
  type SolarDesignDetails,
} from './solarDesigner/SolarDesignDetailsModals';
import {
  type FaceSummary,
  type WorkingFace,
  DesignerTool,
  roofModelProviderLabel,
} from './solarDesigner/types';
import MapAttribution from './solarDesigner/MapAttribution';
import { useSolarDesignerLayout } from './solarDesigner/useSolarDesignerLayout';
import { SOLAR_INSPECTOR_DIALOG_HASHES } from './solarDesigner/useInspectorDialog';
import InspectorNavigation, {
  type InspectorDestination,
  type InspectorFocusRequest,
} from './solarDesigner/InspectorNavigation';
import { solarCanonicalJson } from '@payaca/helpers/solarCommittedInputs';
import {
  computeSolarSavings,
  demandCappedSelfUse,
  generationBasisLabel,
  resolveSolarSavingsRates,
  solarGenerationBasis,
  solarSavingsRegion,
  withUsNetMeteringYearOne,
} from './solarSavings';
import { solarPanelAppliancesForLayout } from './solarDesigner/solarEquipment';
import { createEstimateFromSavedDesign } from './solarDesigner/createEstimateFromSavedDesign';
import {
  PANEL_SIZE_CHANGED_MESSAGE,
  SITE_CHANGED_MESSAGE,
  SOURCE_NOT_RECORDED_MESSAGE,
  recoverSolarDesign,
  savedDraftRecoveryBaseline,
  sessionRecoveryBaseline,
  type RecoverySite,
  type SolarDesignRecoveryBaseline,
  type SolarDesignRecoverySource,
} from './solarDesigner/recoverSolarDesign';

type SolarDesignerContentProps = {
  projectId: string;
  projectPublicId: string;
  /** MANUAL: a roof drawn by hand on a plain map, with no analysis behind it. */
  basis?: 'ANALYSIS' | 'MANUAL';
  designId?: string;
  queryDesignId?: string;
  initialDraft?: SolarDesignDraftState;
  draftRevision: number;
  draftName: string;
  draftDescription?: string | null;
  /** An estimate names the design it came from, so a used design is kept. */
  draftUsedByEstimate?: boolean;
  staleDraft: boolean;
  /**
   * What the saved draft itself recorded, so a draft that is already stale
   * when the page opens is checked against its own site and module size.
   */
  savedSource?: {
    sourceAnalysisId: string | null;
    site: RecoverySite | null;
    staleReason: 'ANALYSIS_REFRESHED' | 'SITE_CHANGED' | null;
  };
  recoverySite?: RecoverySite;
  readRecoverySource?: () => Promise<SolarDesignRecoverySource>;
  onRecoveredSource?: (source: ProjectSolarPotential) => void;
  siteAddressId?: string;
  household?: HouseholdElectricity | null;
  postcode?: string | null;
  generateSolarGeometry: (
    input: {
      projectId: string;
      location?: SolarGeometryLocation;
      forceRefresh?: boolean;
    },
    options?: { onSettled?: () => void }
  ) => void;
  isGeneratingGeometry: boolean;
  generationError?: string | null;
  designerStateRefreshFailed?: boolean;
  solarPotential: ProjectSolarPotential;
  /** The page's own exit, to the project's Property tab (Cancel does not use it). */
  onExit: () => void;
  /** Seeds the "couldn't trace a precise model" soft alert (confirm flow). */
  initialGeneratedWithoutGeometry?: boolean;
};

/** Undefined follows the recommendation; null is an explicit removal. */
const pickedInverter = (
  inverter: SolarInverterSelection
): string | null | undefined => {
  if (inverter.mode === 'none') return null;
  return inverter.mode === 'selected' ? inverter.applianceId : undefined;
};

const inverterSelection = (
  picked: string | null | undefined,
  recommendedApplianceId: string | undefined
): SolarInverterSelection => {
  if (picked === null) return { mode: 'none' };
  const applianceId = picked ?? recommendedApplianceId;
  return applianceId
    ? { mode: 'selected', applianceId }
    : { mode: 'automatic' };
};

/** Minor currency units → major (e.g. pence → GBP). */
/**
 * Roof colouring for strings: each string's palette key, each rendered panel's
 * key, and one label at each string's first member panel.
 */
export const stringColouring = (
  electrical: Pick<SolarElectricalTopology, 'strings'> | null | undefined,
  workingPanels: readonly {
    id?: string;
    corners?: readonly { lat: number; lng: number }[];
  }[]
): {
  colourKeys: Map<string, number>;
  panelStringKeys: (number | null)[];
  stringLabels: { panelIndex: number; label: string }[];
} => {
  const indexById = new Map<string, number>();
  workingPanels.forEach((panel, index) => {
    if (panel.id != null) indexById.set(panel.id, index);
  });
  const panelStringKeys: (number | null)[] = workingPanels.map(() => null);
  const stringLabels: { panelIndex: number; label: string }[] = [];
  const colourKeys = solarStringColourKeys(
    electrical?.strings ?? [],
    workingPanels,
    SOLAR_STRING_PALETTE.length
  );
  (electrical?.strings ?? []).forEach((string) => {
    const key = colourKeys.get(string.id) ?? 0;
    let first: number | undefined;
    for (const id of string.panelIds) {
      const index = indexById.get(id);
      if (index == null) continue;
      panelStringKeys[index] = key;
      first ??= index;
    }
    if (first != null)
      stringLabels.push({ panelIndex: first, label: string.name });
  });
  return { colourKeys, panelStringKeys, stringLabels };
};

/**
 * Roofs for the Electrical tab in the roof list's order, so "Roof N" matches
 * the Roof tab; any committed face missing from the list follows.
 */
export const electricalFaces = (
  faceSummaries: readonly { faceId: number; azimuthDegrees: number }[],
  workingFaces: readonly { id: number; azimuthDegrees: number }[]
): { id: number; azimuthDegrees: number }[] => {
  const ordered = faceSummaries.map((face) => ({
    id: face.faceId,
    azimuthDegrees: face.azimuthDegrees,
  }));
  const listed = new Set(ordered.map((face) => face.id));
  return [
    ...ordered,
    ...workingFaces
      .filter((face) => !listed.has(face.id))
      .map(({ id, azimuthDegrees }) => ({ id, azimuthDegrees })),
  ];
};

const toMajorUnits = (price: {
  value: number;
  currency: { exponent: number };
}) => price.value / Math.pow(10, price.currency.exponent);

/** Google's imagery quality enum → a customer-meaningful pill. HIGH = ≤10 cm/pixel aerial imagery. */
const imageryQualityPill = (
  quality: string
): { label: string; colour: 'green' | 'gray' } =>
  quality === 'HIGH'
    ? { label: 'High detail', colour: 'green' }
    : { label: 'Standard detail', colour: 'gray' };

const formatImageryMonth = (isoDate: string) => {
  const parsed = new Date(isoDate);
  return Number.isNaN(parsed.getTime()) ? null : formatDate(parsed, 'MMM yyyy');
};

/** Keyboard → tool bindings (single letters, matching the rail's hints). */
const TOOL_KEYS: Record<string, DesignerTool> = {
  v: 'select',
  q: 'draw',
  o: 'obstacle',
  m: 'move',
  b: 'trace',
  p: 'pin',
};

/** What a blocked recovery differs on, in a few words. */
const RECOVERY_BLOCKED_LINES: Record<string, string> = {
  [SITE_CHANGED_MESSAGE]: 'Site moved since this layout was drawn',
  [PANEL_SIZE_CHANGED_MESSAGE]: 'Roof analysis now uses a different panel size',
  [SOURCE_NOT_RECORDED_MESSAGE]:
    "This layout can't be matched to the new roof analysis",
};

/** One notice card over the map: a short line and the actions that resolve it. */
const NoticeLine: FC<{
  children: ReactNode;
  actions?: ReactNode;
  colour?: 'red' | 'blue';
  onDismiss?: () => void;
}> = (props) => (
  <div className="pointer-events-auto max-w-full rounded-lg bg-white shadow-md">
    <Alert
      colour={props.colour ?? 'red'}
      iconName={props.colour === 'blue' ? 'info-circle.3' : 'alert-triangle.3'}
      className="[&>div]:items-center"
    >
      <div className="flex items-center gap-2">
        <div className="flex min-w-0 flex-auto flex-wrap items-center gap-x-3 gap-y-1">
          <div className="min-w-0">
            <Text>{props.children}</Text>
          </div>
          {props.actions && (
            <div className="flex shrink-0 flex-wrap gap-2">{props.actions}</div>
          )}
        </div>
        {props.onDismiss && (
          <Button
            variant="quaternary"
            size="sm"
            aria-label="Dismiss"
            className="size-8 shrink-0 justify-center p-0"
            onClick={props.onDismiss}
          >
            <UntitledIcon name="x-close" className="size-4" />
          </Button>
        )}
      </div>
    </Alert>
  </div>
);

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  return (
    target.isContentEditable ||
    ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName) ||
    !!target.closest(
      '[role="dialog"], [role="menu"], [role="listbox"], [role="radiogroup"]'
    )
  );
};

/** One line; the notice's action opens the roof's settings. */
const REPACK_REFUSED =
  'Repack refused · check the roof pitch and reference edge';

const SolarDesignerContent: FC<SolarDesignerContentProps> = (props) => {
  const {
    projectId,
    projectPublicId,
    solarPotential,
    initialGeneratedWithoutGeometry,
    initialDraft,
    isGeneratingGeometry,
  } = props;
  const basis = props.basis ?? 'ANALYSIS';
  const manualRoof = basis === 'MANUAL';
  const roofRecoveryMessage = solarRoofRecoveryMessage(
    solarPotential.roofRecoveryReason,
    basis
  );
  const deleteDesign = useDeleteSolarDesign(projectId);
  const saveDraft = useSaveSolarDesignDraft(
    projectPublicId,
    projectId,
    props.queryDesignId
  );
  const [draftRevision, setDraftRevision] = useState(props.draftRevision);
  const [isPreparingEstimate, setIsPreparingEstimate] = useState(false);
  const [isSwitchingSource, setIsSwitchingSource] = useState(false);
  const [editingDetails, setEditingDetails] = useState(false);
  const [duplicatingDesign, setDuplicatingDesign] = useState(false);
  const [deletingDesign, setDeletingDesign] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const rotationBlockedError = useRef<string | null>(null);
  const blockWhileRotating = useCallback((message: string) => {
    rotationBlockedError.current = message;
    setActionError(message);
  }, []);
  const [analysisConflict, setAnalysisConflict] = useState(false);
  const [isRecovering, setIsRecovering] = useState(false);
  const [recoveryDetailsOpen, setRecoveryDetailsOpen] = useState(false);
  const [recoveryBaseline] = useState<SolarDesignRecoveryBaseline>(() =>
    props.staleDraft
      ? savedDraftRecoveryBaseline({
          state: initialDraft,
          sourceAnalysisId: props.savedSource?.sourceAnalysisId,
          sourceSite: props.savedSource?.site,
          staleReason: props.savedSource?.staleReason,
          currentSite: props.recoverySite,
          currentSource: solarPotential,
        })
      : sessionRecoveryBaseline(solarPotential, props.recoverySite)
  );
  const needsRecovery = !manualRoof && (props.staleDraft || analysisConflict);
  const history = useHistory();
  const [inspectorFocus, setInspectorFocus] =
    useState<InspectorFocusRequest | null>(null);
  const inspectorSequence = useRef(0);
  const clearInspectorFocus = useCallback(() => setInspectorFocus(null), []);
  const [canvasMode, setCanvasMode] = useState<CanvasMode>('inspect');
  // A US account must never see UK-tariff GBP figures.
  const account = useAccount();
  const savingsRegion = solarSavingsRegion(account?.region);
  const savingsRates = resolveSolarSavingsRates(savingsRegion, {
    retailTariffMinorPerKwh: account?.solarRetailTariffMinorPerKwh,
    exportTariffMinorPerKwh: account?.solarExportTariffMinorPerKwh,
  });
  const currencySymbol = savingsRates.currencySymbol;
  const [household, setHousehold] = useState<HouseholdElectricity | null>(
    props.household ?? null
  );
  useEffect(() => {
    // Seed from the query once it arrives; do not overwrite in-progress edits.
    setHousehold((current) => current ?? props.household ?? null);
  }, [props.household]);

  const {
    centre,
    panels,
    roofSegments,
    imageryQuality,
    imageryDate,
    configurations,
    defaultConfigurationIndex,
    panelWidthMeters: genericPanelWidthMeters,
    panelHeightMeters: genericPanelHeightMeters,
    panelCapacityWatts,
    recommendedPanelAppliances: allPanelAppliances,
    recommendedBatteryAppliances,
    carbonOffsetFactorKgPerMwh,
    netMeteringAllowed,
    geometry,
    fluxOverlay,
    rgbOverlay,
  } = solarPotential;

  const defaultConfig = configurations[defaultConfigurationIndex];

  const [pickedPanelApplianceId, setPickedPanelApplianceId] = useState<
    string | undefined
  >(initialDraft?.selectedPanelApplianceId ?? undefined);
  /** A hand-drawn roof has no packed module; it is drawn and sized with the chosen panel itself. */
  const manualPanelSpec = manualRoof
    ? (
        (pickedPanelApplianceId
          ? allPanelAppliances.find(
              (r) => r.appliance.id === pickedPanelApplianceId
            )
          : undefined) ?? allPanelAppliances[0]
      )?.appliance.productSpec
    : undefined;
  const panelWidthMeters = manualRoof
    ? (manualPanelSpec?.solarPanelWidthM ?? 1.045)
    : (geometry?.panelDimensions?.widthMeters ?? genericPanelWidthMeters);
  const panelHeightMeters = manualRoof
    ? (manualPanelSpec?.solarPanelHeightM ?? 1.879)
    : (geometry?.panelDimensions?.heightMeters ?? genericPanelHeightMeters);

  /**
   * The capacity the layout was actually packed with: the account's
   * preferred panel where one is set, else Google's generic module.
   */
  const packedCapacityWatts = manualRoof
    ? (manualPanelSpec?.panelCapacityW ?? null)
    : (geometry?.panelDimensions?.capacityWatts ?? panelCapacityWatts ?? null);

  const manualPanelKwh = useCallback(
    (face: WorkingFace): number | null => {
      if (
        savingsRegion !== 'uk' ||
        !props.postcode ||
        packedCapacityWatts == null
      )
        return null;
      const zone = resolvePostcodeToZone(props.postcode);
      if (!zone) return null;
      const kk = getKkValue(
        zone,
        face.pitchDegrees,
        orientationFromSouth(face.azimuthDegrees)
      );
      return kk == null ? null : (packedCapacityWatts / 1000) * kk;
    },
    [savingsRegion, props.postcode, packedCapacityWatts]
  );

  /**
   * The MIS 3002 annual output of ONE panel on each traced face, computed at
   * generate time (panel kWp × the face's Kk × its shade factor).
   */
  const mcsPerPanelByFace = useMemo(() => {
    const byFace = new Map<number, number>();
    (geometry?.packedPanels ?? []).forEach((panel) => {
      if (panel.mcsYearlyEnergyKwh != null && !byFace.has(panel.faceIndex)) {
        byFace.set(panel.faceIndex, panel.mcsYearlyEnergyKwh);
      }
    });
    return byFace;
  }, [geometry]);

  /**
   * `configIndex` is the panel-count slider's selection; null means the
   * user has toggled individual panels ("custom" mode). Unused in geometry
   * mode.
   */
  const [configIndex, setConfigIndex] = useState<number | null>(
    initialDraft
      ? initialDraft.configIndex
      : ((defaultConfig && defaultConfigurationIndex) ?? null)
  );
  const [selectedPanelIndexes, setSelectedPanelIndexes] = useState<number[]>(
    () =>
      initialDraft?.selectedPanelIndexes ??
      panels
        .slice(0, defaultConfig?.panelsCount ?? panels.length)
        .map((_, i) => i)
  );
  const [includeBattery, setIncludeBattery] = useState(
    initialDraft?.includeBattery ?? false
  );
  const [monthlyPolicy, setUsNetMeteringPolicy] =
    useState<SolarUsNetMeteringPolicy | null>(
      initialDraft?.monthlyPolicy ?? null
    );
  const [placementRules, setPlacementRules] = useState<SolarPlacementRules>(
    () =>
      parseSolarPlacementRules(
        initialDraft?.placementRules ?? account?.solarPlacementRules,
        savingsRegion
      )
  );
  const [panelSelection, setPanelSelection] = useState<PanelSelection>(null);
  /**
   * Array rules and mounting changes repack on their own (Undo covers
   * regret): the roofs to repack (`all` after an Array rules change) and the
   * Undo step of the edit that asked, which the repack joins.
   */
  const [autoRepack, setAutoRepack] = useState<{
    all: boolean;
    faceIds: number[];
    step: number;
  } | null>(null);
  /** "N roofs repacked · Undo" after an Array rules change moved panels. */
  const [repackNotice, setRepackNotice] = useState<number | null>(null);
  const [roofBearingRuleByFace, setRoofBearingRuleByFace] = useState<
    Record<string, string>
  >(initialDraft?.roofBearingRuleByFace ?? {});
  const recordNewFacesPacked = useCallback(
    (faceIds: number[], rules: SolarPlacementRules) => {
      setRoofBearingRuleByFace((previous) =>
        recordRoofBearingRules(faceIds, rules, previous, true)
      );
    },
    []
  );
  const [snapPanelsToArray, setSnapPanelsToArray] = useState(false);
  const [showFluxOverlay, setShowFluxOverlay] = useState(false);
  /** Analysed-imagery underlay, ON by default when the row stored one. */
  const [showAnalysedImagery, setShowAnalysedImagery] = useState(true);
  const [showPanelsLayer, setShowPanelsLayer] = useState(true);
  const generatedWithoutGeometry = initialGeneratedWithoutGeometry ?? false;
  const isGenerateError = !!props.generationError;
  // Regeneration replaces the working source, so save current edits first.
  const generateSolarGeometry: SolarDesignerContentProps['generateSolarGeometry'] =
    (input, options) => {
      setIsSwitchingSource(true);
      void persistDesign()
        .then(() =>
          props.generateSolarGeometry(
            { ...input, forceRefresh: true },
            {
              onSettled: () => {
                setIsSwitchingSource(false);
                options?.onSettled?.();
              },
            }
          )
        )
        .catch(() => {
          setIsSwitchingSource(false);
          options?.onSettled?.();
        });
    };

  const historyInteractionRef = useRef<(() => void) | undefined>(undefined);
  const recordHistoryInteraction = useCallback(
    () => historyInteractionRef.current?.(),
    []
  );
  const {
    tool,
    setTool,
    drawTemplate,
    setDrawTemplate,
    cycleDrawTemplate,
    workingPanels,
    workingFaces,
    layoutSnapshot,
    committedLayoutSnapshot,
    isArrayRotating,
    onCommitSelectedFaceRotation,
    onBeginSelectedFaceRotation,
    onEndSelectedFaceRotation,
    onCancelSelectedFaceRotation,
    layoutReady,
    restoreLayout,
    excludedFaceIds,
    onToggleSelectedFaceIncluded,
    workingObstacles,
    faceOffsets,
    onSetSelectedFaceAnchor,
    selectedFaceId,
    selectedObstacleIndex,
    manualMode,
    repositionMode,
    repositionPoint,
    layoutDirty,
    geometryMode,
    fitKey,
    setSelectedFaceId,
    setSelectedObstacleIndex,
    setWorkingPanels,
    setLayoutDirty,
    setRepositionPoint,
    onRotateSelectedFace,
    onStraightenSelectedFace,
    onRoofMoved,
    onFaceMoved,
    drawPlane,
    onFaceDrawStart,
    onClearSelectedFace,
    onFaceBoundaryChanged,
    onFaceDrawn,
    onTemplateDrawn,
    onSetSelectedFacePitch,
    onSetSelectedFaceMounting,
    onConfirmSelectedRoofPlane,
    onFootprintPick,
    onDeleteSelectedFace,
    onObstacleDrawn,
    onObstacleRemove,
    onResetToAuto,
    startManualDraw,
    confirmReposition,
    repackAllFaces,
    repackAffectedFaces,
    repackFace,
    setFaceRulesOverride,
    onOrientFaceGroup,
  } = useSolarDesignerLayout({
    initialLayout: initialDraft?.layout,
    designId: props.designId,
    geometry,
    centre,
    panelWidthMeters,
    panelHeightMeters,
    panelCapacityWatts: packedCapacityWatts,
    projectId,
    setConfigIndex,
    generateSolarGeometry,
    recordInteraction: recordHistoryInteraction,
    onFacesPacked: recordNewFacesPacked,
    placementRules,
    region: savingsRegion,
    estimatePanelKwh: manualRoof ? manualPanelKwh : undefined,
  });

  const editingPanelIndexes = panelSelectionIndexes(
    panelSelection,
    workingPanels
  );
  useEffect(() => {
    // Explicit deletion/undo can remove the inspected target. Do not leave the
    // angle editor open with no roof; a rotation still owns its target.
    if (selectedFaceId == null && !isArrayRotating) setCanvasMode('inspect');
  }, [selectedFaceId, isArrayRotating]);
  useEffect(() => {
    if (!isArrayRotating && canvasMode === 'inspect')
      setActionError((error) =>
        error === rotationBlockedError.current ? null : error
      );
  }, [isArrayRotating, canvasMode]);
  const groupOrientations = new Set(
    panelSelection?.kind === 'group'
      ? workingPanels
          .filter((panel) => panel.faceId === panelSelection.faceId)
          .map((panel) => panel.orientation)
      : []
  );
  // Tool/roof selection changes end a panel selection; membership itself is faceId.
  useEffect(() => {
    if (tool !== 'select' || panelSelection?.faceId !== selectedFaceId) {
      setPanelSelection(null);
    }
  }, [tool, selectedFaceId, panelSelection?.faceId]);

  /**
   * The stored MCS per-panel figure, carried onto a working face only while
   * that face is still the traced plane it was measured on.
   */
  const mcsPerPanelByFaceId = useMemo(
    () =>
      mcsPerPanelByWorkingFace({
        workingFaces,
        tracedFaces: geometry?.faces,
        mcsPerPanelByTracedFace: mcsPerPanelByFace,
        postcode: savingsRegion === 'uk' ? (props.postcode ?? null) : null,
        capacityWatts: packedCapacityWatts,
      }),
    [
      workingFaces,
      geometry,
      mcsPerPanelByFace,
      props.postcode,
      savingsRegion,
      packedCapacityWatts,
    ]
  );

  // Building footprints near the site — for repositioning and tracing.
  const { footprints: buildingFootprints } =
    useGetBuildingFootprints(projectId);
  const footprintRings = useMemo(
    () => buildingFootprints.map((footprint) => footprint.ring),
    [buildingFootprints]
  );
  const hasFootprints = footprintRings.length > 0;

  // Wrong-building guard for the pin tool: compares the title parcel at the
  // site address against the one under the pin.
  const { parcel: addressParcel } = useGetTitleParcelAt(projectId, null, {
    enabled: repositionMode,
  });
  const pinPoint = repositionPoint ?? centre ?? null;
  const { parcel: pinParcel } = useGetTitleParcelAt(projectId, pinPoint, {
    enabled: repositionMode && !!pinPoint,
  });
  const showParcelMismatchHint =
    repositionMode && parcelsMismatch(addressParcel, pinParcel);

  const recommendedPanelAppliances = useMemo(() => {
    return solarPanelAppliancesForLayout(
      allPanelAppliances,
      geometry,
      geometryMode
    );
  }, [allPanelAppliances, geometry, geometryMode]);

  const [pickedInverterApplianceId, setPickedInverterApplianceId] = useState<
    string | null | undefined
  >(initialDraft ? pickedInverter(initialDraft.inverter) : undefined);
  const [pickedBatteryApplianceId, setPickedBatteryApplianceId] = useState<
    string | undefined
  >(initialDraft?.selectedBatteryApplianceId ?? undefined);
  const [batteryQuantity, setBatteryQuantity] = useState(
    initialDraft?.batteryQuantity ?? 1
  );

  const panelsCount = geometryMode
    ? workingPanels.filter((panel) => panel.included).length
    : selectedPanelIndexes.length;
  const selectedPanelAppliance = useMemo(
    () =>
      pickedPanelApplianceId
        ? recommendedPanelAppliances.find(
            (r) => r.appliance.id === pickedPanelApplianceId
          )
        : recommendedPanelAppliances[0],
    [recommendedPanelAppliances, pickedPanelApplianceId]
  );
  const quotedModules = resolveQuotedSolarModules(
    {
      panelsCount,
      panelCapacityWatts: packedCapacityWatts ?? 0,
      preservePanelCount: geometryMode,
    },
    selectedPanelAppliance?.appliance.productSpec.panelCapacityW
      ? {
          panelCapacityW:
            selectedPanelAppliance.appliance.productSpec.panelCapacityW,
        }
      : null
  );
  const [savedRecommendationState, setSavedRecommendationState] = useState(
    props.initialDraft
  );
  const savedRecommendationLayout = useMemo(
    () =>
      savedRecommendationState && props.designId
        ? restoreSolarPanelIdentities(
            savedRecommendationState.layout,
            props.designId
          )
        : savedRecommendationState?.layout,
    [savedRecommendationState, props.designId]
  );
  const effectivePanelApplianceId = effectiveSolarApplianceId(
    pickedPanelApplianceId,
    selectedPanelAppliance
  );
  const savedSizingReady =
    !props.designId ||
    (!!savedRecommendationState &&
      JSON.stringify(savedRecommendationLayout) ===
        JSON.stringify(layoutSnapshot) &&
      JSON.stringify(savedRecommendationState.selectedPanelIndexes) ===
        JSON.stringify(selectedPanelIndexes) &&
      savedRecommendationState.configIndex === configIndex &&
      savedRecommendationState.includeBattery === includeBattery &&
      (savedRecommendationState.batteryQuantity ?? 1) === batteryQuantity &&
      savedSolarPanelSelectionMatches(
        savedRecommendationState.selectedPanelApplianceId,
        pickedPanelApplianceId,
        recommendedPanelAppliances[0]
      ));
  const inverterMatches = useSolarDesignerInverters(
    projectId,
    panelsCount,
    packedCapacityWatts,
    includeBattery,
    props.designId,
    geometryMode
      ? new Set(
          workingPanels
            .filter((panel) => panel.included)
            .map((panel) => panel.faceId)
        ).size
      : new Set(
          panels
            .filter((_, index) => selectedPanelIndexes.includes(index))
            .map((panel) => panel.segmentIndex)
        ).size,
    pickedPanelApplianceId,
    props.designId
      ? {
          revision: draftRevision,
          basis,
          sourceAnalysisId: manualRoof ? null : solarPotential.sourceAnalysisId,
          sourceAnalysisVersion: manualRoof
            ? null
            : solarPotential.sourceAnalysisVersion,
        }
      : undefined,
    savedSizingReady,
    batteryQuantity
  );
  const { recommendedInverterAppliances } = inverterMatches;

  const inverterOptions = useMemo(() => {
    const selectedExcluded = inverterMatches.excludedInverterAppliances.find(
      (r) => r.appliance.id === pickedInverterApplianceId
    );
    return selectedExcluded
      ? [...recommendedInverterAppliances, selectedExcluded]
      : recommendedInverterAppliances;
  }, [
    recommendedInverterAppliances,
    inverterMatches.excludedInverterAppliances,
    pickedInverterApplianceId,
  ]);
  const selectedInverterAppliance = useAutomaticSolarEquipment(
    JSON.stringify([
      props.designId,
      basis,
      solarPotential.sourceAnalysisId,
      solarPotential.sourceAnalysisVersion,
    ]),
    pickedInverterApplianceId,
    pickedInverterApplianceId
      ? inverterOptions
      : inverterOptions.filter(
          (r) => r.appliance.productSpec.category === 'INVERTER'
        ),
    !savedSizingReady ||
      inverterMatches.isLoading ||
      !inverterMatches.hasCheckedRecommendations
  );
  const selectedBatteryAppliance = useMemo(() => {
    if (selectedInverterAppliance?.appliance.productSpec.pvConversion)
      return selectedInverterAppliance;
    return pickedBatteryApplianceId
      ? recommendedBatteryAppliances.find(
          (r) => r.appliance.id === pickedBatteryApplianceId
        )
      : recommendedBatteryAppliances[0];
  }, [
    recommendedBatteryAppliances,
    pickedBatteryApplianceId,
    selectedInverterAppliance,
  ]);

  const onPreviewArrayRotation = useCallback(
    (degrees: number) => {
      // A clipped count refreshes recommendations. Retain the equipment currently
      // shown until the installer explicitly chooses a different product.
      if (selectedPanelAppliance)
        setPickedPanelApplianceId(
          (id) => id ?? selectedPanelAppliance.appliance.id
        );
      if (selectedInverterAppliance)
        setPickedInverterApplianceId(
          (id) => id ?? selectedInverterAppliance.appliance.id
        );
      if (selectedBatteryAppliance)
        setPickedBatteryApplianceId(
          (id) => id ?? selectedBatteryAppliance.appliance.id
        );
      onRotateSelectedFace(degrees);
    },
    [
      selectedPanelAppliance,
      selectedInverterAppliance,
      selectedBatteryAppliance,
      onRotateSelectedFace,
    ]
  );

  const calculatePerformance = useCalculateSolarDesignPerformance(
    props.projectPublicId
  );
  const calculationEpoch = useRef(0);
  useEffect(
    () => () => {
      calculationEpoch.current++;
    },
    [props.designId]
  );
  const [weatherGeneration, setWeatherGeneration] = useState<
    SolarWeatherGeneration | undefined
  >(props.initialDraft?.weatherGeneration);
  const [electrical, setElectrical] = useState(
    allocatedSolarElectrical(props.initialDraft?.electrical) ?? undefined
  );
  const [electricalNamespace] = useState(
    () => props.designId ?? crypto.randomUUID()
  );
  const electricalAdoptedRef = useRef(
    props.initialDraft?.electrical.mode === 'allocated'
  );
  const [allocationHighlightIds, setAllocationHighlightIds] = useState<
    string[]
  >([]);
  const [selectedStringId, setSelectedStringId] = useState<string | null>(null);
  const [allocationHoverIds, setAllocationHoverIds] = useState<string[]>([]);
  const [allocationError, setAllocationError] = useState<string | null>(null);
  /** Electrical's Auto-string set allocation up; string it once mounted. */
  const [autoStringPending, setAutoStringPending] = useState(false);
  // Allocated edits target immutable group/unit profiles. Recommendation refreshes must
  // not silently replace the legacy whole-design fields, including saved nulls.
  const wholeDesignEquipmentRef = useRef({
    selectedPanelApplianceId: initialDraft?.selectedPanelApplianceId ?? null,
    inverter: initialDraft?.inverter ?? { mode: 'automatic' },
  });
  const draftState = useMemo<SolarDesignDraftState>(() => {
    const state: SolarDesignDraftState = {
      electrical: { mode: 'wholeDesign' },
      weatherGeneration,
      layout: committedLayoutSnapshot,
      selectedPanelApplianceId: effectivePanelApplianceId,
      inverter: inverterSelection(
        pickedInverterApplianceId,
        selectedInverterAppliance?.appliance.id
      ),
      selectedBatteryApplianceId:
        pickedBatteryApplianceId ??
        selectedBatteryAppliance?.appliance.id ??
        null,
      includeBattery,
      configIndex,
      batteryQuantity,
      selectedPanelIndexes,
      placementRules,
      // Deleted roofs must not leave provenance in persistence or Undo snapshots.
      roofBearingRuleByFace: Object.fromEntries(
        Object.entries(roofBearingRuleByFace).filter(([id]) =>
          committedLayoutSnapshot.workingFaces.some(
            (face) => face.id === Number(id)
          )
        )
      ),
      monthlyPolicy,
    };
    if (!electrical)
      wholeDesignEquipmentRef.current = {
        selectedPanelApplianceId: state.selectedPanelApplianceId,
        inverter: state.inverter,
      };
    return electrical
      ? {
          ...state,
          ...wholeDesignEquipmentRef.current,
          ...solarElectricalLayout(
            state.layout,
            electrical,
            electricalNamespace,
            wholeDesignEquipmentRef.current.selectedPanelApplianceId
          ),
        }
      : state;
  }, [
    electrical,
    electricalNamespace,
    committedLayoutSnapshot,
    weatherGeneration,
    pickedInverterApplianceId,
    pickedBatteryApplianceId,
    effectivePanelApplianceId,
    selectedInverterAppliance,
    selectedBatteryAppliance,
    includeBattery,
    configIndex,
    batteryQuantity,
    selectedPanelIndexes,
    placementRules,
    roofBearingRuleByFace,
    monthlyPolicy,
  ]);
  const draftTopology = allocatedSolarElectrical(draftState.electrical);
  const roofStringColouring = useMemo(
    () => stringColouring(draftTopology, workingPanels),
    [draftTopology, workingPanels]
  );
  /**
   * Latest electrical draft between renders, so a batch of changes (Auto-string)
   * applies each change on top of the previous one rather than a stale closure.
   */
  const electricalDraftRef = useRef(draftState);
  electricalDraftRef.current = draftState;
  const restoreDesign = useCallback(
    (
      restoredState: SolarDesignDraftState,
      reason: SolarDesignRestoreReason
    ) => {
      const upgraded =
        electricalAdoptedRef.current &&
        restoredState.electrical.mode !== 'allocated'
          ? upgradeSolarDesignElectrical(restoredState, electricalNamespace)
          : restoredState;
      if ('error' in upgraded && typeof upgraded.error === 'string') {
        setAllocationError(upgraded.error);
        return null;
      }
      const { state, clearedInstanceIds } = restoreSolarDesignHistory(
        draftState,
        upgraded,
        reason
      );
      setAllocationError(
        clearedInstanceIds.length
          ? 'Equipment history restored. Revised inverter inputs and string connections were cleared; declare and reconnect them before installation review.'
          : null
      );
      wholeDesignEquipmentRef.current = {
        selectedPanelApplianceId: state.selectedPanelApplianceId,
        inverter: state.inverter,
      };
      setElectrical(allocatedSolarElectrical(state.electrical) ?? undefined);
      setSelectedStringId(null);
      setAllocationHighlightIds([]);
      setAllocationHoverIds([]);
      setWeatherGeneration(state.weatherGeneration);
      restoreLayout(state.layout);
      setPickedPanelApplianceId(state.selectedPanelApplianceId ?? undefined);
      setPickedInverterApplianceId(pickedInverter(state.inverter));
      setPickedBatteryApplianceId(
        state.selectedBatteryApplianceId ?? undefined
      );
      setIncludeBattery(state.includeBattery);
      setBatteryQuantity(state.batteryQuantity);
      setConfigIndex(state.configIndex);
      setSelectedPanelIndexes(state.selectedPanelIndexes);
      setPlacementRules(state.placementRules);
      setRoofBearingRuleByFace(state.roofBearingRuleByFace);
      setUsNetMeteringPolicy(state.monthlyPolicy);
      setPanelSelection(null);
      // A queued auto-repack belongs to the state being left behind.
      setAutoRepack(null);
      setRepackNotice(null);
      return state;
    },
    [restoreLayout, electricalNamespace, draftState]
  );
  const [historyReady, setHistoryReady] = useState(false);
  const [repackError, setRepackError] = useState<string | null>(null);
  useEffect(() => {
    if (layoutReady && !inverterMatches.isLoading) setHistoryReady(true);
  }, [layoutReady, inverterMatches.isLoading]);
  const designHistory = useDesignHistory(
    draftState,
    restoreDesign,
    historyReady
  );
  historyInteractionRef.current = designHistory.recordInteraction;
  const { markSaved } = designHistory;
  const { mutateAsync: saveDraftAsync } = saveDraft;
  const hasLocalEdits = designHistory.isDirty || isArrayRotating;
  // Previously enabled Equipment callbacks must respect the current preview.
  const rotationPreviewRef = useRef(isArrayRotating);
  rotationPreviewRef.current = isArrayRotating;
  const electricalLayoutChangeDisabledReason = isArrayRotating
    ? 'Apply or cancel rotation before configuring allocations or revising panels.'
    : undefined;
  // Save acknowledgement invalidates this query independently of the mutation.
  // Completion focus must outlive that refetch and its revision-keyed sizing
  // refresh, rather than being consumed before the autosave debounce starts.
  const refreshingDesignerState =
    useIsFetching({
      queryKey: solarDesignerStateKey(projectId, props.queryDesignId),
      exact: true,
    }) > 0;

  // Apply an incoming draft only when this editor is clean — never
  // overwrite edits the user is still making.
  useEffect(() => {
    if (
      props.draftRevision <= draftRevision ||
      tool !== 'select' ||
      canvasMode !== 'inspect' ||
      !props.initialDraft ||
      needsRecovery ||
      isRecovering ||
      hasLocalEdits ||
      saveDraft.isPending
    )
      return;
    restoreDesign(props.initialDraft, 'remote');
    // A clean background refresh replaces data, not the roof the user is
    // inspecting. Undo and explicit restores retain their existing behaviour.
    if (
      selectedFaceId != null &&
      props.initialDraft.layout.workingFaces.some(
        (face) => face.id === selectedFaceId
      )
    )
      setSelectedFaceId(selectedFaceId);
    setDraftRevision(props.draftRevision);
    setSavedRecommendationState(props.initialDraft);
    markSaved(props.initialDraft);
  }, [
    hasLocalEdits,
    draftRevision,
    markSaved,
    props.draftRevision,
    props.initialDraft,
    restoreDesign,
    saveDraft.isPending,
    needsRecovery,
    isRecovering,
    selectedFaceId,
    setSelectedFaceId,
    tool,
    canvasMode,
  ]);

  const [confirmingReplacement, setConfirmingReplacement] = useState(false);
  const persistDesign = useCallback(
    async (replacePrevious = false) => {
      const state = draftState;
      setActionError(null);
      try {
        if (isArrayRotating) {
          rotationBlockedError.current =
            'Finish rotating the panel array before saving.';
          throw new Error(rotationBlockedError.current);
        }
        if (needsRecovery) {
          throw new Error(
            'Choose Keep my layout to save with the current roof analysis.'
          );
        }
        if (props.staleDraft && !replacePrevious) {
          throw new Error('Confirm replacing the previous draft first.');
        }
        const data = await saveDraftAsync({
          designId: props.designId,
          basis,
          sourceAnalysisId: manualRoof ? null : solarPotential.sourceAnalysisId,
          sourceAnalysisVersion: manualRoof
            ? null
            : solarPotential.sourceAnalysisVersion,
          expectedRevision: draftRevision,
          state,
        });
        setDraftRevision(data.saveSolarDesignDraft.revision);
        const savedElectrical = allocatedSolarElectrical(
          (data.saveSolarDesignDraft.state as SolarDesignDraftState).electrical
        );
        if (savedElectrical)
          setElectrical((current) =>
            current
              ? {
                  ...current,
                  equipmentSelections: current.equipmentSelections.map(
                    (selection) =>
                      savedElectrical.equipmentSelections.find(
                        (saved) =>
                          saved.id === selection.id &&
                          saved.applianceId === selection.applianceId
                      ) ?? selection
                  ),
                }
              : current
          );
        setSavedRecommendationState(state);
        markSaved(state);
        return data.saveSolarDesignDraft;
      } catch (error) {
        const message = extractErrorMessage(error).message;
        if (/roof analysis.*changed/i.test(message)) setAnalysisConflict(true);
        setActionError(message);
        throw error;
      }
    },
    [
      basis,
      manualRoof,
      draftRevision,
      draftState,
      markSaved,
      saveDraftAsync,
      solarPotential.sourceAnalysisId,
      solarPotential.sourceAnalysisVersion,
      props.staleDraft,
      props.designId,
      needsRecovery,
      isArrayRotating,
    ]
  );

  const saveDetails = useCallback(
    async (details: SolarDesignDetails) => {
      // Only send from a clean editor — a stale payload must never overwrite
      // working layout changes.
      if (
        !props.designId ||
        draftRevision === 0 ||
        props.staleDraft ||
        needsRecovery ||
        isRecovering ||
        hasLocalEdits ||
        saveDraft.isPending
      ) {
        throw new Error('Wait for the current design to finish saving.');
      }
      const state = draftState;
      setActionError(null);
      try {
        const data = await saveDraftAsync({
          designId: props.designId,
          name: details.name.trim(),
          description: details.description.trim() || null,
          basis,
          sourceAnalysisId: manualRoof ? null : solarPotential.sourceAnalysisId,
          sourceAnalysisVersion: manualRoof
            ? null
            : solarPotential.sourceAnalysisVersion,
          expectedRevision: draftRevision,
          state,
        });
        setDraftRevision(data.saveSolarDesignDraft.revision);
        // This updates the saved fingerprint without altering the undo stack.
        markSaved(state);
      } catch (error) {
        const message = extractErrorMessage(error).message;
        if (/roof analysis.*changed/i.test(message)) setAnalysisConflict(true);
        setActionError(message);
        throw error;
      }
    },
    [
      basis,
      manualRoof,
      hasLocalEdits,
      draftRevision,
      draftState,
      markSaved,
      props.designId,
      props.staleDraft,
      saveDraft.isPending,
      needsRecovery,
      isRecovering,
      saveDraftAsync,
      solarPotential.sourceAnalysisId,
      solarPotential.sourceAnalysisVersion,
    ]
  );

  const draftFingerprint = useMemo(
    () => JSON.stringify(draftState),
    [draftState]
  );
  useAutoSaveDesign({
    ready: historyReady && !isArrayRotating,
    dirty: designHistory.isDirty,
    revision: draftRevision,
    stale: props.staleDraft || analysisConflict || isRecovering,
    pending: saveDraft.isPending || calculatePerformance.isPending,
    fingerprint: draftFingerprint,
    save: persistDesign,
  });
  useEffect(() => {
    if (!hasLocalEdits) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', beforeUnload);
    const unblock = history.block((next) => {
      const current = history.location;
      // Inspector editors are hash-history entries within this exact design,
      // not exits. Keep the leave guard for query/design changes, route state
      // changes and unrecognised fragments, even while an editor is open.
      const inspectorHashes = ['', ...SOLAR_INSPECTOR_DIALOG_HASHES];
      if (
        next.pathname === current.pathname &&
        next.search === current.search &&
        solarCanonicalJson(next.state ?? null) ===
          solarCanonicalJson(current.state ?? null) &&
        inspectorHashes.includes(current.hash) &&
        inspectorHashes.includes(next.hash)
      )
        return;
      return 'You have unsaved design changes. Leave without saving?';
    });
    return () => {
      window.removeEventListener('beforeunload', beforeUnload);
      unblock();
    };
  }, [hasLocalEdits, history]);

  const selectedIndexSet = useMemo(
    () => new Set(selectedPanelIndexes),
    [selectedPanelIndexes]
  );

  /**
   * Satellite estimate for a repacked analysed layout (first layout or
   * Repack), from the analysis' own per-roof yield. A Calculate result
   * (weather generation) always takes precedence.
   */
  const analysisEstimate = useMemo(
    () =>
      geometryMode && !manualRoof && !weatherGeneration
        ? analysisGenerationEstimate({
            faces: workingFaces,
            panels: workingPanels,
            dimensions: {
              widthMeters: panelWidthMeters ?? 1.045,
              heightMeters: panelHeightMeters ?? 1.879,
              capacityWatts: packedCapacityWatts ?? 400,
            },
            analysisPanels: geometry?.packedPanels,
            analysisFaceCount: geometry?.faces.length,
          })
        : null,
    [
      geometryMode,
      manualRoof,
      weatherGeneration,
      workingFaces,
      workingPanels,
      panelWidthMeters,
      panelHeightMeters,
      packedCapacityWatts,
      geometry,
    ]
  );

  /**
   * Per-face numbers for the summary's face list, from the working state so
   * they follow every edit.
   */
  const unorderedFaceSummaries = useMemo<FaceSummary[]>(
    () =>
      workingFaces.map((face) => {
        const onFace = workingPanels.filter((p) => p.faceId === face.id);
        const included = onFace.filter((p) => p.included);
        const mcsPerPanel = mcsPerPanelByFaceId.get(face.id) ?? null;
        const { annualKwh } = solarGenerationBasis(
          included.map((p) => ({
            annualAcKwh: p.annualAcKwh,
            mcsYearlyEnergyKwh: mcsPerPanel,
            faceIndex: face.id,
          }))
        );
        let currentFaceAnnualKwh: number | null = null;
        if (weatherGeneration) {
          currentFaceAnnualKwh =
            currentSolarWeatherSnapshot(draftState)?.arrays.find(
              (array) => array.roofId === face.id
            )?.response.outputs.ac_annual ?? null;
        } else if (
          mcsPerPanel != null ||
          generationStatusHasResult(
            faceGenerationStatus(face, onFace, {
              widthMeters: panelWidthMeters ?? 1.045,
              heightMeters: panelHeightMeters ?? 1.879,
              capacityWatts: packedCapacityWatts ?? 400,
            })
          )
        ) {
          currentFaceAnnualKwh = annualKwh;
        } else if (mcsPerPanel == null) {
          currentFaceAnnualKwh = analysisEstimate?.byFace.get(face.id) ?? null;
        }
        const roof = physicalRoofPlane(face);
        return {
          faceId: face.id,
          azimuthDegrees: roof.azimuthDegrees,
          pitchDegrees: roof.pitchDegrees,
          areaMeters2: roofSurfaceAreaMeters2(
            face.areaMeters2,
            roof.pitchDegrees
          ),
          panelsAvailable: onFace.length,
          panelsIncluded: included.length,
          annualKwh: currentFaceAnnualKwh,
          systemKwp:
            packedCapacityWatts != null
              ? (included.length * packedCapacityWatts) / 1000
              : null,
          annualSunAccessPercent: weatherGeneration
            ? null
            : (face.shading?.annualSunAccessPercent ?? null),
          photoAgreement: face.photoAgreement ?? null,
          rulesOverride: !!face.rulesOverride,
          planeFit: face.planeFit,
        };
      }),
    [
      workingFaces,
      workingPanels,
      packedCapacityWatts,
      mcsPerPanelByFaceId,
      panelWidthMeters,
      panelHeightMeters,
      weatherGeneration,
      draftState,
      analysisEstimate,
    ]
  );
  const faceSummaries = useStableFaceOrder(unorderedFaceSummaries);
  const allocationFaces = useMemo(
    () => electricalFaces(faceSummaries, draftState.layout.workingFaces),
    [faceSummaries, draftState.layout.workingFaces]
  );
  let panelSelectionLabel: string | undefined;
  if (panelSelection?.kind === 'group') {
    const roofNumber =
      faceSummaries.findIndex((face) => face.faceId === panelSelection.faceId) +
      1;
    panelSelectionLabel = `Roof ${roofNumber} panel group`;
  } else if (panelSelection?.kind === 'panels') {
    panelSelectionLabel = `Roof ${faceSummaries.findIndex((face) => face.faceId === selectedFaceId) + 1} · ${editingPanelIndexes.length} panel${editingPanelIndexes.length === 1 ? '' : 's'} selected`;
  } else if (selectedFaceId != null) {
    const roofNumber =
      faceSummaries.findIndex((face) => face.faceId === selectedFaceId) + 1;
    panelSelectionLabel = `Roof ${roofNumber} selected`;
  }

  /** Keep the best `count` panels on a face by yield and exclude the rest — the per-face slider. */
  const onSetFacePanelCount = useCallback(
    (faceId: number, count: number) => {
      setWorkingPanels((prev) => {
        const ranked = prev
          .map((panel, index) => ({ panel, index }))
          .filter(({ panel }) => panel.faceId === faceId)
          .sort((a, b) => b.panel.annualAcKwh - a.panel.annualAcKwh);
        const keep = new Set(ranked.slice(0, count).map(({ index }) => index));
        return prev.map((panel, index) =>
          panel.faceId === faceId
            ? { ...panel, included: keep.has(index) }
            : panel
        );
      });
      setLayoutDirty(true);
    },
    [setWorkingPanels, setLayoutDirty]
  );

  const faceIndexById = useMemo(() => {
    const map = new Map<number, number>();
    workingFaces.forEach((face, index) => map.set(face.id, index));
    return map;
  }, [workingFaces]);

  // Panels for the roof view, with faceIndex resolved to the current face
  // array position (−1 if off-face).
  const renderPackedPanels = useMemo(
    () =>
      workingPanels.map((panel) => ({
        corners: panel.corners,
        faceIndex: faceIndexById.get(panel.faceId) ?? -1,
        segmentIndex: panel.segmentIndex,
        orientation: panel.orientation,
        annualAcKwh: panel.annualAcKwh,
      })),
    [workingPanels, faceIndexById]
  );

  const facePolygons = useMemo(
    () => workingFaces.map((face) => face.polygon),
    [workingFaces]
  );

  const selectedFaceIndexForView =
    selectedFaceId != null ? (faceIndexById.get(selectedFaceId) ?? null) : null;

  // Indices (into workingPanels) of the currently included panels.
  const includedWorkingIndexes = useMemo(
    () =>
      workingPanels.reduce<number[]>((acc, panel, index) => {
        if (panel.included) acc.push(index);
        return acc;
      }, []),
    [workingPanels]
  );

  /**
   * The design's annual generation and which method it came from (MIS 3002
   * 4.1.8(b) on a UK traced roof; satellite yield otherwise).
   */
  const generation = useMemo(() => {
    if (geometryMode) {
      return solarGenerationBasis(
        workingPanels
          .filter((panel) => panel.included)
          .map((panel) => ({
            annualAcKwh: panel.annualAcKwh,
            mcsYearlyEnergyKwh: mcsPerPanelByFaceId.get(panel.faceId) ?? null,
            faceIndex: panel.faceId,
          }))
      );
    }
    return solarGenerationBasis(
      panels.filter((_, index) => selectedIndexSet.has(index))
    );
  }, [
    geometryMode,
    workingPanels,
    panels,
    selectedIndexSet,
    mcsPerPanelByFaceId,
  ]);
  const weather = currentSolarWeatherSnapshot(draftState);
  let generationStatus = geometryMode
    ? solarGenerationAvailability({
        faces: workingFaces,
        panels: workingPanels,
        excludedFaceIds,
        dimensions: {
          widthMeters: panelWidthMeters ?? 1.045,
          heightMeters: panelHeightMeters ?? 1.879,
          capacityWatts: packedCapacityWatts ?? 400,
        },
      })
    : 'legacy_unverified';
  if (weatherGeneration) {
    generationStatus = 'needs_recalculation';
    if (weatherGeneration.status === 'unavailable')
      generationStatus = 'unavailable';
    if (weather) generationStatus = 'valid';
  }
  // A repacked analysed layout shows the analysis' per-roof yield as a
  // satellite estimate instead of asking for a recalculation; only a stale
  // Calculate (weather) result still asks for one.
  // The estimate is non-null only when every included roof is either
  // recorded, repacked on the analysis or a lost-record analysed roof, so it
  // stands whether the design reads "needs recalculation" or "unavailable"
  // (as long as no included panel has lost its roof).
  const usesAnalysisEstimate =
    !weatherGeneration &&
    (generationStatus === 'needs_recalculation' ||
      (generationStatus === 'unavailable' &&
        workingPanels.every(
          (panel) =>
            !panel.included ||
            workingFaces.some((face) => face.id === panel.faceId)
        ))) &&
    generation.basis === 'satellite' &&
    analysisEstimate != null;
  if (usesAnalysisEstimate) generationStatus = 'valid';
  let annualKwh: number | null = generation.annualKwh;
  if (weatherGeneration) annualKwh = weather?.annualAcKwh ?? null;
  else if (usesAnalysisEstimate) annualKwh = analysisEstimate.annualKwh;
  const currentGenerationBasis =
    generationStatus === 'legacy_unverified' && generation.basis !== 'mcs'
      ? `Historical ${generationBasisLabel(generation.basis, basis).toLowerCase()}`
      : generationBasisLabel(generation.basis, basis);
  const generationAvailable =
    generationStatusHasResult(generationStatus) ||
    (savingsRegion === 'uk' && generation.basis === 'mcs');
  const liveCalculationInputs = useRef({
    rotating: isArrayRotating,
    designId: props.designId,
    fingerprint: draftFingerprint,
    revision: draftRevision,
  });
  liveCalculationInputs.current = {
    rotating: isArrayRotating,
    designId: props.designId,
    fingerprint: draftFingerprint,
    revision: draftRevision,
  };
  const calculateUnsupported =
    manualRoof ||
    !geometryMode ||
    !selectedPanelAppliance ||
    panelsCount === 0 ||
    workingFaces
      .filter(
        (face) =>
          !excludedFaceIds.includes(face.id) &&
          workingPanels.some(
            (panel) => panel.included && panel.faceId === face.id
          )
      )
      .some(
        (face) =>
          !face.roofPlane ||
          !face.appliedMounting ||
          face.pitchSource === 'assumed'
      );
  const calculateWeather = async () => {
    if (
      !props.designId ||
      isArrayRotating ||
      designHistory.isDirty ||
      saveDraft.isPending
    )
      return;
    const requested = liveCalculationInputs.current;
    const requestEpoch = calculationEpoch.current;
    setActionError(null);
    try {
      const data = await calculatePerformance.mutateAsync({
        designId: props.designId,
        expectedRevision: requested.revision,
      });
      // A result never replaces edits made while the request was in flight.
      if (calculationEpoch.current !== requestEpoch) return;
      if (
        liveCalculationInputs.current.designId !== requested.designId ||
        liveCalculationInputs.current.fingerprint !== requested.fingerprint ||
        liveCalculationInputs.current.revision !== requested.revision ||
        liveCalculationInputs.current.rotating
      ) {
        setActionError(
          'The design changed during calculation. Reload the saved design before calculating again.'
        );
        return;
      }
      const parsed = solarDesignDraftStateSchema.safeParse(
        data.calculateSolarDesignPerformance.state
      );
      if (!parsed.success)
        throw new Error('Refresh Payaca to read this weather estimate.');
      setWeatherGeneration(parsed.data.weatherGeneration);
      setDraftRevision(data.calculateSolarDesignPerformance.revision);
      markSaved({
        ...draftState,
        weatherGeneration: parsed.data.weatherGeneration,
      });
    } catch (error) {
      if (calculationEpoch.current === requestEpoch)
        setActionError(extractErrorMessage(error).message);
    }
  };

  const monthlyScenarioGeneration = solarMonthlySourceForDesign(draftState);

  // Legacy (pre-Electrical) lines: appliance, unit price and count.
  const legacyEquipmentLines = useMemo(() => {
    if (draftTopology || !selectedPanelAppliance) return null;
    const lines = [
      {
        appliance: selectedPanelAppliance.appliance,
        quantity: quotedModules.panelsCount,
      },
    ];
    if (selectedInverterAppliance)
      lines.push({
        appliance: selectedInverterAppliance.appliance,
        quantity: selectedInverterAppliance.appliance.productSpec.pvConversion
          ? batteryQuantity
          : (selectedInverterAppliance.quantity ?? 1),
      });
    if (
      includeBattery &&
      selectedBatteryAppliance &&
      selectedBatteryAppliance.appliance.id !==
        selectedInverterAppliance?.appliance.id
    )
      lines.push({
        appliance: selectedBatteryAppliance.appliance,
        quantity: batteryQuantity,
      });
    return lines.filter((line) => line.quantity > 0);
  }, [
    draftTopology,
    selectedPanelAppliance,
    selectedInverterAppliance,
    selectedBatteryAppliance,
    includeBattery,
    quotedModules.panelsCount,
    batteryQuantity,
  ]);
  const { data: electricalPrices } = useSolarElectricalPrices(
    draftTopology
      ? solarElectricalEquipmentQuantities(draftState)
      : legacyEquipmentLines &&
          new Map(
            legacyEquipmentLines.map((line) => [
              line.appliance.id,
              line.quantity,
            ])
          )
  );
  // Current prices for the actual included groups and Electrical units, never
  // the legacy global panel.
  const systemCostGbp = useMemo(() => {
    if (draftTopology) {
      const price = solarElectricalEquipmentCost(
        draftState,
        electricalPrices ?? []
      );
      if (price) return toMajorUnits(price);
      // Each line at the server's totalPrice(quantity) where it has loaded,
      // else the catalogue unit price × count, as the legacy path does.
      const quantities = solarElectricalEquipmentQuantities(draftState);
      if (!quantities) return null;
      let cost = 0;
      for (const [id, quantity] of quantities) {
        const priced = electricalPrices?.find(
          (entry) => entry.id === id && entry.quantity === quantity
        );
        if (priced?.items.length) {
          cost += toMajorUnits(priced.totalPrice);
          continue;
        }
        const appliance = [
          ...recommendedPanelAppliances,
          ...inverterOptions,
          ...inverterMatches.excludedInverterAppliances,
          ...recommendedBatteryAppliances,
        ].find((item) => item.appliance.id === id)?.appliance;
        if (!appliance) return null;
        cost += toMajorUnits(appliance.totalPrice) * quantity;
      }
      return cost;
    }
    if (!legacyEquipmentLines) return null;
    // The server's totalPrice(quantity) rounds VAT once per line, as the
    // estimate does; unit price × count stands in until it loads.
    return legacyEquipmentLines.reduce((cost, line) => {
      const priced = electricalPrices?.find(
        (entry) =>
          entry.id === line.appliance.id && entry.quantity === line.quantity
      );
      return (
        cost +
        (priced
          ? toMajorUnits(priced.totalPrice)
          : toMajorUnits(line.appliance.totalPrice) * line.quantity)
      );
    }, 0);
  }, [
    legacyEquipmentLines,
    draftState,
    draftTopology,
    electricalPrices,
    recommendedPanelAppliances,
    inverterOptions,
    inverterMatches.excludedInverterAppliances,
    recommendedBatteryAppliances,
  ]);

  const installedCapacityKwh = includeBattery
    ? installedBatteryCapacityKwh(
        selectedBatteryAppliance?.appliance.productSpec.usableCapacityKwh,
        batteryQuantity
      )
    : null;
  const allocatedCapacityKwh =
    installedCapacityKwh == null
      ? null
      : eessAllocatedCapacityKwh(
          installedCapacityKwh,
          parseSolarEessSettings(account?.solarEessSettings)
            .selfConsumptionAllocationPercent
        );
  const selfConsumption =
    annualKwh !== null && savingsRegion === 'uk' && generation.basis === 'mcs'
      ? estimateSelfConsumption({
          occupancyArchetype: household?.electricityOccupancyArchetype ?? null,
          annualConsumptionKwh:
            household?.annualElectricityConsumptionKwh ?? null,
          annualGenerationKwh: annualKwh,
          batteryUsableKwh:
            allocatedCapacityKwh != null && allocatedCapacityKwh > 0
              ? allocatedCapacityKwh
              : null,
        })
      : null;
  // The fixed self-use share before any cap; the credit ledger caps it per
  // month itself, so this is what its form starts from.
  const assumedSelfUseFraction = selfConsumptionFraction(includeBattery);
  // US: home self-use can never exceed the household's demand.
  const usDemandKwh =
    savingsRegion === 'us'
      ? (household?.annualElectricityConsumptionKwh ?? null)
      : null;
  const savingsSelfUseFraction = selfConsumption
    ? (includeBattery
        ? (selfConsumption.withBattery ?? selfConsumption.pvOnly)
        : selfConsumption.pvOnly
      ).percent / 100
    : demandCappedSelfUse(assumedSelfUseFraction, usDemandKwh, annualKwh);
  // US with a saved credit policy: the ledger's year-1 benefit is the figure.
  // Derived at read time: the saved policy only records the choice.
  const currentMonthlyPolicy =
    savingsRegion === 'us' && monthlyPolicy
      ? derivedUsNetMeteringPolicy(monthlyPolicy, {
          site: centre,
          annualDemandKwh: household?.annualElectricityConsumptionKwh ?? null,
          selfUseFraction: assumedSelfUseFraction,
          exportCentsPerKwh: savingsRates.exportTariffMinorPerKwh,
        })
      : null;
  const usLedger = currentMonthlyPolicy
    ? calculateSolarUsNetMeteringPolicy(
        currentMonthlyPolicy,
        monthlyScenarioGeneration,
        savingsRates.retailTariffMinorPerKwh,
        undefined,
        weatherGeneration !== undefined
          ? solarUsNetMeteringWeatherProvenance
          : undefined
      )
    : null;
  const usLedgerYearOneUsd =
    usLedger?.status === 'available'
      ? usLedger.snapshot.firstYearBenefitUsd
      : null;
  const scalarSavings =
    annualKwh === null || (draftTopology && systemCostGbp === null)
      ? null
      : computeSolarSavings({
          selfConsumptionFraction: savingsSelfUseFraction,
          annualAcKwh: annualKwh,
          panelsCount,
          region: savingsRegion,
          includeBattery,
          systemCostGbp,
          retailTariffMinorPerKwh: savingsRates.retailTariffMinorPerKwh,
          exportTariffMinorPerKwh: savingsRates.exportTariffMinorPerKwh,
        });
  const savings = withUsNetMeteringYearOne(
    scalarSavings,
    usLedgerYearOneUsd,
    systemCostGbp ?? panelsCount * savingsRates.indicativeCostPerPanelMajor
  );

  const onSelectConfig = (index: number) => {
    const config = configurations[index];
    if (!config) return;
    setConfigIndex(index);
    setSelectedPanelIndexes(
      panels.slice(0, config.panelsCount).map((_, i) => i)
    );
  };

  const onPanelClick = (
    panelIndex: number,
    modifiers?: { metaKey: boolean; ctrlKey: boolean; shiftKey?: boolean }
  ) => {
    if (geometryMode) {
      if (canvasMode !== 'inspect' || tool !== 'select') return;
      if (isArrayRotating) {
        blockWhileRotating(
          'Finish rotating the panel array before changing the selection.'
        );
        return;
      }
      const panel = workingPanels[panelIndex];
      if (!panel) return;
      setSelectedFaceId(panel.faceId);
      setSelectedObstacleIndex(null);
      setPanelSelection((current) =>
        selectPanel(
          current,
          workingPanels,
          panelIndex,
          !!(modifiers?.metaKey || modifiers?.ctrlKey),
          !!modifiers?.shiftKey
        )
      );
      return;
    }
    setConfigIndex(null);
    setSelectedPanelIndexes((current) =>
      current.includes(panelIndex)
        ? current.filter((i) => i !== panelIndex)
        : [...current, panelIndex].sort((a, b) => a - b)
    );
  };

  const onPanelsMoved = (
    delta: { dLat: number; dLng: number },
    removedPanelIndexes: number[]
  ) => {
    if (
      !editingPanelIndexes.length ||
      isArrayRotating ||
      canvasMode !== 'inspect' ||
      (!delta.dLat && !delta.dLng && !removedPanelIndexes.length)
    )
      return;
    recordHistoryInteraction();
    setWorkingPanels((current) =>
      moveSelectedPanels(
        current,
        editingPanelIndexes,
        delta,
        removedPanelIndexes
      )
    );
    if (removedPanelIndexes.length) setPanelSelection(null);
    setLayoutDirty(true);
  };

  const deleteSelectedPanels = useCallback(() => {
    if (isArrayRotating || canvasMode !== 'inspect') return;
    if (panelSelection?.kind !== 'panels') return;
    recordHistoryInteraction();
    setWorkingPanels((current) => {
      const removed = new Set(panelSelectionIndexes(panelSelection, current));
      return current.filter((_, index) => !removed.has(index));
    });
    setPanelSelection(null);
    setSelectedFaceId(null);
    setLayoutDirty(true);
  }, [
    isArrayRotating,
    canvasMode,
    panelSelection,
    recordHistoryInteraction,
    setWorkingPanels,
    setSelectedFaceId,
    setLayoutDirty,
  ]);

  const onObstacleClick = (index: number) => {
    if (canvasMode !== 'inspect' || isArrayRotating) return;
    if (isArrayRotating) {
      blockWhileRotating(
        'Finish rotating the panel array before changing the selection.'
      );
      return;
    }
    setPanelSelection(null);
    if (tool === 'obstacle') {
      onObstacleRemove(index);
      return;
    }
    setSelectedObstacleIndex(index);
    setSelectedFaceId(null);
  };

  // Where the panel-count slider sits in custom mode: nearest configuration
  // to the current panel count.
  const sliderIndex = useMemo(() => {
    if (configIndex != null) return configIndex;
    let nearest = 0;
    configurations.forEach((config, index) => {
      if (
        Math.abs(config.panelsCount - panelsCount) <
        Math.abs(configurations[nearest].panelsCount - panelsCount)
      ) {
        nearest = index;
      }
    });
    return nearest;
  }, [configIndex, configurations, panelsCount]);

  const selectedFace =
    selectedFaceId != null
      ? workingFaces.find((face) => face.id === selectedFaceId)
      : undefined;
  const selectedFaceOffset =
    selectedFaceId != null ? (faceOffsets[selectedFaceId] ?? 0) : 0;

  // Orientation for a new roof area: the drawn plane's azimuth, else the
  // nearest existing face, else the largest face, else south.
  const addFaceAzimuthDegrees =
    drawPlane?.azimuthDegrees ??
    selectedFace?.azimuthDegrees ??
    workingFaces[0]?.azimuthDegrees ??
    180;

  const fluxOverlayProp = useMemo(
    () =>
      fluxOverlay
        ? {
            url: fluxOverlay.url,
            resourceKey: `${solarPotential.sourceAnalysisId}:${solarPotential.sourceAnalysisVersion}:flux`,
            bounds: {
              sw: {
                lat: fluxOverlay.bounds.south,
                lng: fluxOverlay.bounds.west,
              },
              ne: {
                lat: fluxOverlay.bounds.north,
                lng: fluxOverlay.bounds.east,
              },
            },
          }
        : undefined,
    [
      fluxOverlay,
      solarPotential.sourceAnalysisId,
      solarPotential.sourceAnalysisVersion,
    ]
  );

  const queryClient = useQueryClient();
  /** Overlay URLs are short-lived signatures; a failed image load asks for fresh ones. */
  const onOverlayImageError = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: projectKeys.solarPotential(projectId),
    });
  }, [queryClient, projectId]);

  const rgbUnderlayProp = useMemo(
    () =>
      rgbOverlay
        ? {
            url: rgbOverlay.url,
            resourceKey: `${solarPotential.sourceAnalysisId}:${solarPotential.sourceAnalysisVersion}:rgb`,
            bounds: {
              sw: {
                lat: rgbOverlay.bounds.south,
                lng: rgbOverlay.bounds.west,
              },
              ne: {
                lat: rgbOverlay.bounds.north,
                lng: rgbOverlay.bounds.east,
              },
            },
          }
        : undefined,
    [
      rgbOverlay,
      solarPotential.sourceAnalysisId,
      solarPotential.sourceAnalysisVersion,
    ]
  );

  const {
    mutate: createEstimate,
    isPending: isCreatingEstimate,
    error: createError,
    reset: resetCreateEstimate,
  } = useCreateEstimate({
    cacheProjectId: projectId,
    onSuccess: (data) => {
      history.push(
        `/projects/${projectId}/estimates/${data.createEstimate.id}`
      );
    },
  });

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        event.defaultPrevented ||
        saveDraft.isPending ||
        isRecovering ||
        isPreparingEstimate ||
        isSwitchingSource ||
        isCreatingEstimate
      )
        return;
      if (
        !(event.ctrlKey || event.metaKey) ||
        isTypingTarget(event.target) ||
        event.key.toLowerCase() !== 'z'
      )
        return;
      event.preventDefault();
      if (event.shiftKey) designHistory.redo();
      else designHistory.undo();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [
    designHistory,
    isPreparingEstimate,
    isSwitchingSource,
    isCreatingEstimate,
    saveDraft.isPending,
    isRecovering,
  ]);

  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const leaveDesigner = useCallback(() => {
    history.push(`/projects/${projectId}/design`);
  }, [history, projectId]);

  const onCancel = useCallback(() => {
    if (hasLocalEdits) {
      setConfirmingCancel(true);
      return;
    }
    leaveDesigner();
  }, [hasLocalEdits, leaveDesigner]);

  const onCreateEstimate = async () => {
    if (isArrayRotating) {
      blockWhileRotating(
        'Finish rotating the panel array before creating an estimate.'
      );
      return;
    }
    if (roofRecoveryMessage) return;
    setIsPreparingEstimate(true);
    try {
      await createEstimateFromSavedDesign(
        projectPublicId,
        persistDesign,
        createEstimate
      );
    } catch {
      setIsPreparingEstimate(false);
      return;
    }
    setIsPreparingEstimate(false);
  };

  // Keyboard shortcuts: geometry mode only, never while typing in a field.
  useEffect(() => {
    if (
      !geometryMode ||
      saveDraft.isPending ||
      isRecovering ||
      isPreparingEstimate ||
      isSwitchingSource ||
      isCreatingEstimate
    )
      return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (
        e.defaultPrevented ||
        e.metaKey ||
        e.ctrlKey ||
        e.altKey ||
        isTypingTarget(e.target)
      ) {
        return;
      }
      if (e.key === 'Escape') {
        if (isArrayRotating) {
          blockWhileRotating(
            'Finish rotating the panel array before changing the selection.'
          );
          return;
        }
        if (canvasMode !== 'inspect') {
          setCanvasMode('inspect');
          return;
        }
        // First press cancels an armed tool; otherwise clears the selection.
        if (tool !== 'select') {
          setTool('select');
        } else {
          setPanelSelection(null);
          setSelectedFaceId(null);
          setSelectedObstacleIndex(null);
        }
        return;
      }
      const key = e.key.toLowerCase();
      if (key === 'h') {
        setShowPanelsLayer((on) => !on);
        return;
      }
      // Q arms the outline tool; further presses cycle its shape (outline → rect →
      // gable → hip → shed).
      if (key === 'q' && tool === 'draw') {
        cycleDrawTemplate();
        return;
      }
      if (TOOL_KEYS[key]) {
        if (
          canvasMode !== 'inspect' ||
          (tool !== 'select' && TOOL_KEYS[key] !== tool)
        )
          return;
        if (isArrayRotating) {
          blockWhileRotating(
            'Finish rotating the panel array before changing tools.'
          );
          return;
        }
        if (!(manualRoof && TOOL_KEYS[key] === 'pin')) setTool(TOOL_KEYS[key]);
        return;
      }
      if ((e.key === 'Delete' || e.key === 'Backspace') && tool === 'select') {
        if (isArrayRotating || canvasMode !== 'inspect') return;
        if (
          e.target instanceof HTMLElement &&
          e.target.closest('button, a, [role="tab"]')
        )
          return;
        e.preventDefault();
        if (panelSelection?.kind === 'panels') deleteSelectedPanels();
        else if (panelSelection?.kind === 'group') return;
        else if (selectedFaceId != null) onDeleteSelectedFace();
        else if (selectedObstacleIndex != null) {
          onObstacleRemove(selectedObstacleIndex);
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [
    canvasMode,
    geometryMode,
    manualRoof,
    saveDraft.isPending,
    isRecovering,
    isPreparingEstimate,
    isSwitchingSource,
    isCreatingEstimate,
    tool,
    setTool,
    cycleDrawTemplate,
    selectedFaceId,
    selectedObstacleIndex,
    onDeleteSelectedFace,
    onObstacleRemove,
    setSelectedFaceId,
    setSelectedObstacleIndex,
    panelSelection,
    deleteSelectedPanels,
    isArrayRotating,
    blockWhileRotating,
  ]);

  if (!centre) return null;

  let saveStatus =
    draftRevision > 0 && !props.staleDraft ? 'Saved' : 'New design';
  if (!historyReady) saveStatus = 'Loading design…';
  else if (props.staleDraft || needsRecovery)
    saveStatus = 'Automatic saving paused';
  else if (saveDraft.isError) saveStatus = 'Could not save';
  else if (isArrayRotating) saveStatus = 'Unsaved rotation';
  else if (designHistory.isDirty || draftRevision === 0) saveStatus = 'Saving…';

  const processingLabel: string | undefined = isGeneratingGeometry
    ? 'Analysing roof imagery…'
    : undefined;

  const {
    addingFace,
    addingObstacle,
    packedPanelsForView,
    showFootprints,
    footprintsForView,
    footprintPickActive,
    obstacleEditMode,
    faceEditMode,
    staticFaceOutlines,
    boundaryEditFaceIndex,
    repositionForView,
    panelClickDisabled,
    parcelForView,
    showAllFaceOutlines,
    grabFacesForView,
    moveWholeRoofForView,
  } = deriveRoofViewProps({
    geometryMode,
    tool,
    showPanelsLayer,
    hasFootprints,
    footprintRings,
    renderPackedPanels,
    selectedFaceIndexForView,
    parcelRing: geometry?.parcel?.ring ?? null,
  });

  const stringLabelsInteractive =
    !panelClickDisabled &&
    canvasMode === 'inspect' &&
    tool === 'select' &&
    !isArrayRotating;
  const stringAtPanel = (panelIndex: number) => {
    const id = workingPanels[panelIndex]?.id;
    return id == null
      ? undefined
      : draftTopology?.strings.find((item) => item.panelIds.includes(id));
  };

  const showMergedBuildingNotice =
    geometryMode && !repositionMode && !!geometry?.spansMultipleProperties;

  /** The array the estimate would be priced at, for the products warning. */
  const arrayKwp =
    quotedModules.panelCapacityWatts > 0
      ? (quotedModules.panelsCount * quotedModules.panelCapacityWatts) / 1000
      : null;

  // Only blocking operations belong here. Autosave and the revision-keyed
  // inverter refresh run after every edit; locking the designer for them made
  // panel clicks and drags silently fail for a second or two after each change.
  const isBusy =
    isRecovering ||
    isPreparingEstimate ||
    isSwitchingSource ||
    isCreatingEstimate ||
    !historyReady;
  let detailsDisabledReason: string | null = null;
  if (!props.designId || draftRevision === 0)
    detailsDisabledReason =
      'Wait for this design to save before changing its details.';
  else if (props.staleDraft || needsRecovery)
    detailsDisabledReason =
      'Save your layout with the current roof analysis before changing its details.';
  else if (hasLocalEdits || saveDraft.isPending)
    detailsDisabledReason = 'Wait for your design changes to save.';
  let deleteDisabledReason: string | null = null;
  if (!props.designId || draftRevision === 0)
    deleteDisabledReason = 'Wait for this design to save before deleting it.';
  else if (props.draftUsedByEstimate)
    deleteDisabledReason =
      'An estimate was created from this design, so it cannot be deleted.';
  const productRecommendationWarning =
    (inverterMatches.hasCheckedRecommendations &&
      pickedInverterApplianceId &&
      !selectedInverterAppliance) ||
    (includeBattery && pickedBatteryApplianceId && !selectedBatteryAppliance)
      ? 'A selected product is not in the current recommendations. Review your selection in Equipment before creating an estimate.'
      : null;
  let createDisabledReason: string | null = null;
  if (isArrayRotating)
    createDisabledReason =
      'Finish rotating the panel array before creating an estimate.';
  if (roofRecoveryMessage) createDisabledReason = roofRecoveryMessage;
  else if (props.staleDraft || needsRecovery)
    createDisabledReason =
      'Save your layout with the current roof analysis before creating an estimate.';
  else if (panelsCount === 0)
    createDisabledReason =
      'Add panels to the roof before creating an estimate.';
  else if (!selectedPanelAppliance)
    createDisabledReason =
      'No priced panel matches this layout. Check Equipment before creating an estimate.';
  else if (inverterMatches.isError)
    createDisabledReason =
      'Inverter recommendations could not be loaded. Retry in Equipment.';

  const selectedInverterPhases =
    selectedInverterAppliance?.appliance.productSpec.phases;
  let selectedInverterAcKw =
    selectedInverterAppliance?.totalAcKw ??
    selectedInverterAppliance?.appliance.productSpec.acOutputKw;
  if (selectedInverterAppliance?.appliance.productSpec.pvConversion) {
    selectedInverterAcKw =
      batteryQuantity === 1
        ? selectedInverterAppliance.appliance.productSpec.pvConversion
            .acOutputKw
        : null;
  }
  let inverterPhases: 1 | 3 | null =
    selectedInverterPhases === 1 || selectedInverterPhases === 3
      ? selectedInverterPhases
      : null;
  // With Electrical allocation the output is the sum over its physical units,
  // as the estimate prices them; phases from the units (any three-phase unit
  // means a three-phase supply, unknown counts as single-phase).
  let allocatedInverterAcKw: number | null | undefined;
  if (draftTopology) {
    const allocation = draftTopology;
    const unitSpecs = allocation.inverterInstances.map((instance) => {
      const applianceId = allocation.equipmentSelections.find(
        (item) => item.id === instance.equipmentSelectionId
      )?.applianceId;
      return [
        ...inverterOptions,
        ...inverterMatches.excludedInverterAppliances,
      ].find((item) => item.appliance.id === applianceId)?.appliance
        .productSpec;
    });
    allocatedInverterAcKw =
      unitSpecs.length && unitSpecs.every((spec) => spec?.acOutputKw != null)
        ? Math.round(
            unitSpecs.reduce((sum, spec) => sum + (spec?.acOutputKw ?? 0), 0) *
              100
          ) / 100
        : null;
    selectedInverterAcKw = allocatedInverterAcKw;
    inverterPhases = null;
    if (unitSpecs.length)
      inverterPhases = unitSpecs.some((spec) => spec?.phases === 3) ? 3 : 1;
  }
  const siteMinTemperatureC = siteDesignMinTemperatureC({
    latitude: centre?.lat,
    longitude: centre?.lng,
    countryCode: account?.region,
  });

  let generationMessage = 'Generation is unavailable for this layout.';
  if (weatherGeneration?.status === 'unavailable')
    generationMessage =
      solarWeatherUnavailableMessage(weatherGeneration) ??
      'Weather generation is unavailable.';

  let calculateDisabledReason: string | null = null;
  if (isArrayRotating)
    calculateDisabledReason = 'Finish the rotation before calculating.';
  else if (props.staleDraft || needsRecovery)
    calculateDisabledReason =
      'Resolve the saved design’s roof analysis before calculating.';
  else if (calculateUnsupported)
    calculateDisabledReason =
      'Needs a confirmed roof plane on an analysed roof';
  // Autosave runs after every edit: waiting for it is not worth a line that
  // flashes while typing, so it only shows on hover or focus.
  const calculateWaitsForSave =
    !props.designId ||
    !draftRevision ||
    designHistory.isDirty ||
    saveDraft.isPending;
  const calculateTooltip =
    calculateDisabledReason ??
    (calculateWaitsForSave ? 'Saving the design first' : null);
  let weatherStatus = 'Not calculated yet';
  if (weatherGeneration?.status === 'unavailable')
    weatherStatus =
      solarWeatherUnavailableMessage(weatherGeneration) ??
      'Weather generation is unavailable.';
  else if (weatherGeneration && !weather)
    weatherStatus = 'Needs recalculation for the current inputs';
  else if (weather) weatherStatus = 'Calculated for the current inputs';

  /**
   * ContextPanel asks before the change settles, so the repack runs from an
   * effect once the new rules or mounting are in state. A burst of edits
   * coalesces into one repack, filed under the Undo step of the edit that
   * asked even if a click, Tab or Escape started a new step meanwhile.
   * Reference-edge intent still waits for an explicit Repack.
   */
  const requestAutoRepack = useCallback(
    (scope: 'all' | 'selected') => {
      const step = designHistory.currentInteraction();
      setAutoRepack((current) => ({
        all: scope === 'all' || !!current?.all,
        faceIds: [
          ...new Set([
            ...(current?.faceIds ?? []),
            ...(scope === 'selected' && selectedFaceId != null
              ? [selectedFaceId]
              : []),
          ]),
        ],
        step: current?.step ?? step,
      }));
    },
    [designHistory, selectedFaceId]
  );
  const { recordUnder } = designHistory;
  useEffect(() => {
    if (!autoRepack) return;
    const timer = window.setTimeout(() => {
      setAutoRepack(null);
      if (isArrayRotating) return;
      recordUnder(autoRepack.step);
      setPanelSelection(null);
      if (autoRepack.all) {
        const changed = repackAffectedFaces(placementRules);
        setRepackError(
          changed || workingFaces.length === 0 ? null : REPACK_REFUSED
        );
        setRepackNotice(changed?.length ? changed.length : null);
        return;
      }
      const applied = autoRepack.faceIds.flatMap((faceId) =>
        repackFace(faceId, placementRules)
      );
      setRepackError(
        applied.length || !autoRepack.faceIds.length ? null : REPACK_REFUSED
      );
    }, 400);
    return () => window.clearTimeout(timer);
  }, [
    autoRepack,
    isArrayRotating,
    placementRules,
    recordUnder,
    repackAffectedFaces,
    repackFace,
    workingFaces,
  ]);
  // The notice is about the last change only.
  const repackNoticeFingerprint = useRef<string | null>(null);
  useEffect(() => {
    if (repackNotice == null) {
      repackNoticeFingerprint.current = null;
      return;
    }
    if (repackNoticeFingerprint.current == null)
      repackNoticeFingerprint.current = draftFingerprint;
    else if (repackNoticeFingerprint.current !== draftFingerprint)
      setRepackNotice(null);
  }, [repackNotice, draftFingerprint]);
  /** "Include anyway" (or "Follow rules"): the selected roof's rules override. */
  const setSelectedFaceRulesOverride = (enabled: boolean) => {
    if (isArrayRotating || selectedFaceId == null) return;
    recordHistoryInteraction();
    setPanelSelection(null);
    const applied = setFaceRulesOverride(
      selectedFaceId,
      enabled,
      placementRules
    );
    setRepackError(applied.length > 0 ? null : REPACK_REFUSED);
  };

  const repackSelection = () => {
    if (isArrayRotating) {
      blockWhileRotating(
        'Apply or cancel the rotation before editing the layout.'
      );
      return;
    }
    const blockedReason = solarRepackBlockedReason(
      selectedFace ? [selectedFace] : workingFaces,
      placementRules
    );
    if (blockedReason) {
      setRepackError(blockedReason);
      return;
    }
    recordHistoryInteraction();
    setPanelSelection(null);
    const applied =
      selectedFaceId != null
        ? repackFace(selectedFaceId, placementRules)
        : repackAllFaces({ rules: placementRules });
    setRepackError(applied.length > 0 ? null : REPACK_REFUSED);
  };

  const keepMyLayout = async () => {
    if (!props.readRecoverySource) return;
    setIsRecovering(true);
    setActionError(null);
    try {
      const { source, saved } = await recoverSolarDesign({
        state: draftState,
        revision: draftRevision,
        original: recoveryBaseline,
        readCurrent: props.readRecoverySource,
        save: (input) =>
          saveDraftAsync({
            ...input,
            designId: props.designId,
            basis,
          }),
      });
      setDraftRevision(saved.saveSolarDesignDraft.revision);
      markSaved(draftState);
      props.onRecoveredSource?.(source);
    } catch (error) {
      setActionError(extractErrorMessage(error).message);
    } finally {
      setIsRecovering(false);
    }
  };
  const recoveryLine = recoveryBaseline.known
    ? 'Roof analysis refreshed · your layout is kept but not saving'
    : `${RECOVERY_BLOCKED_LINES[recoveryBaseline.reason] ?? RECOVERY_BLOCKED_LINES[SOURCE_NOT_RECORDED_MESSAGE]}, start a new design`;
  // During recovery a roof-analysis conflict is already what the line says.
  const noticeError =
    actionError &&
    !(needsRecovery && /roof analysis.*changed/i.test(actionError))
      ? actionError
      : null;

  const navigateInspector = (destination: InspectorDestination) => {
    if (
      isArrayRotating &&
      (destination === 'mounting' || destination === 'spacing')
    )
      return;
    setInspectorFocus({ destination, sequence: ++inspectorSequence.current });
    if (destination === 'spacing' || destination === 'tariff') {
      const { pathname, search, state } = history.location;
      history.push({
        pathname,
        search,
        state,
        hash:
          destination === 'spacing'
            ? '#solar-array-rules'
            : '#solar-credit-policy',
      });
    }
  };
  let canvasDisabledReason: string | undefined;
  if (tool !== 'select')
    canvasDisabledReason = 'Choose Done to finish the current tool first.';
  if (canvasMode !== 'inspect')
    canvasDisabledReason = 'Close the angle editor before changing tools.';
  if (isArrayRotating)
    canvasDisabledReason =
      'Apply or cancel the rotation before editing the layout.';

  /**
   * Start over: one undoable layout change that clears every roof face,
   * obstacle and panel, keeps the analysis imagery, and arms Draw roof.
   */
  const designIsEmpty =
    !workingFaces.length && !workingPanels.length && !workingObstacles.length;
  const startOverDisabledReason =
    canvasDisabledReason ??
    (designIsEmpty ? 'There are no roofs to clear.' : undefined);
  const startOver = () => {
    if (startOverDisabledReason) return;
    recordHistoryInteraction();
    restoreLayout({
      ...layoutSnapshot,
      workingFaces: [],
      workingPanels: [],
      workingObstacles: [],
      faceOffsets: {},
      faceLinks: [],
      excludedFaceIds: [],
      geometryEdited: true,
    });
    setLayoutDirty(true);
    setPanelSelection(null);
    setRepackError(null);
    clearInspectorFocus();
    setTool('draw');
  };

  const selectedRoofNumber =
    faceSummaries.findIndex((face) => face.faceId === selectedFaceId) + 1;
  let repackDisabledReason: string | undefined;
  if (tool !== 'select')
    repackDisabledReason = 'Choose Done to finish the current tool first.';
  if (isArrayRotating)
    repackDisabledReason = 'Apply or cancel the rotation before repacking.';
  const roofPicker = (
    <InspectorNavigation
      faces={faceSummaries}
      selectedScopeLabel={panelSelection ? panelSelectionLabel : null}
      selectedFaceId={selectedFaceId}
      excludedFaceIds={excludedFaceIds}
      rotating={isArrayRotating}
      targetLocked={tool !== 'select' || canvasMode !== 'inspect'}
      onSelectFace={(id) => {
        if (isArrayRotating || tool !== 'select' || canvasMode !== 'inspect')
          return;
        clearInspectorFocus();
        setPanelSelection(null);
        setSelectedObstacleIndex(null);
        setSelectedFaceId(id);
      }}
    />
  );

  return (
    <>
      <DesignerWorkspace
        contextKey={tool + ':' + selectedFaceId + ':' + selectedObstacleIndex}
        hasSelection={selectedFaceId != null || selectedObstacleIndex != null}
        hasActiveTool={tool !== 'select'}
        isBusy={isBusy}
        focusRequest={inspectorFocus}
        onTaskChange={clearInspectorFocus}
        missingInverter={
          panelsCount > 0 &&
          !selectedInverterAppliance &&
          !draftTopology?.inverterInstances.length
        }
        inverterTask={draftTopology ? 'electrical' : 'equipment'}
        actions={
          <DesignerActions
            projectId={projectId}
            designName={props.draftName}
            saveStatus={saveStatus}
            canUndo={designHistory.canUndo}
            canRedo={designHistory.canRedo}
            canSave={
              !needsRecovery &&
              historyReady &&
              (saveDraft.isError || props.staleDraft)
            }
            saveLabel={
              props.staleDraft ? 'Replace previous draft' : 'Retry save'
            }
            isSaving={saveDraft.isPending}
            isCreating={isCreatingEstimate || isPreparingEstimate}
            // Header actions that read the saved revision still wait for an
            // in-flight save; the inspector and map never do.
            isBusy={isBusy || saveDraft.isPending}
            createDisabledReason={createDisabledReason}
            detailsDisabledReason={detailsDisabledReason}
            onUndo={designHistory.undo}
            onRedo={designHistory.redo}
            onSave={() => {
              if (props.staleDraft) {
                setConfirmingReplacement(true);
                return;
              }
              void persistDesign().catch(() => undefined);
            }}
            onCreate={() => {
              void onCreateEstimate();
            }}
            onEditDetails={() => setEditingDetails(true)}
            onDuplicate={() => setDuplicatingDesign(true)}
            onDelete={() => setDeletingDesign(true)}
            deleteDisabledReason={deleteDisabledReason}
            onStartOver={geometryMode ? startOver : undefined}
            startOverDisabledReason={startOverDisabledReason ?? null}
            onShiftAllRoofs={
              geometryMode
                ? () => {
                    if (!canvasDisabledReason) setTool('move');
                  }
                : undefined
            }
            shiftAllRoofsDisabledReason={canvasDisabledReason ?? null}
            onBack={onCancel}
          />
        }
        mapToolRail={
          geometryMode && (
            <ToolRail
              layoutDisabledReason={canvasDisabledReason}
              tool={tool}
              setTool={(next) => {
                if (
                  canvasMode !== 'inspect' ||
                  (tool !== 'select' && next !== 'select')
                )
                  return;
                if (isArrayRotating) {
                  blockWhileRotating(
                    'Finish rotating the panel array before changing tools.'
                  );
                  return;
                }
                setTool(next);
              }}
              hasFootprints={hasFootprints}
              canReposition={!manualRoof}
              drawTemplate={drawTemplate}
              setDrawTemplate={setDrawTemplate}
              onStartOver={startOver}
              startOverDisabledReason={startOverDisabledReason}
            />
          )
        }
        mapToolbar={
          geometryMode && (
            <div className="flex min-w-0 flex-col items-start gap-2">
              <CanvasEditingBar
                scopeControl={roofPicker}
                scope={
                  panelSelectionLabel ??
                  (selectedFace ? `Roof ${selectedRoofNumber}` : 'All roofs')
                }
                hasTarget={!!selectedFace && tool === 'select'}
                repackLabel={
                  selectedFaceId != null
                    ? `Repack roof ${selectedRoofNumber}`
                    : 'Repack all roofs'
                }
                repackDisabledReason={repackDisabledReason}
                mode={canvasMode}
                pending={isArrayRotating}
                layoutPending={
                  !autoRepack &&
                  !!selectedFace &&
                  selectedLayoutStatus(
                    selectedFace,
                    placementRules,
                    selectedFaceOffset,
                    roofBearingRulesNeedRepack(
                      [selectedFace.id],
                      placementRules,
                      roofBearingRuleByFace
                    )
                  ).pending
                }
                roofExcluded={
                  selectedFaceId != null &&
                  excludedFaceIds.includes(selectedFaceId)
                }
                onToggleRoofIncluded={onToggleSelectedFaceIncluded}
                onDeleteRoof={onDeleteSelectedFace}
                onAlignLongestEdge={onStraightenSelectedFace}
                onClearRoofPanels={() => {
                  setPanelSelection(null);
                  onClearSelectedFace();
                }}
                angle={selectedFaceOffset}
                referenceAngle={
                  !!(
                    selectedFace &&
                    (selectedFace.roofEdgeAnchor ||
                      selectedFace.appliedRoofEdgeAnchor ||
                      selectedFace.appliedArrayAzimuthDegrees !== undefined)
                  )
                }
                focusEnabled={
                  !isBusy && !hasLocalEdits && !refreshingDesignerState
                }
                focusIdentity={selectedFaceId}
                panelsSelected={panelSelection != null}
                orientation={
                  groupOrientations.size === 1
                    ? [...groupOrientations][0]
                    : null
                }
                onOrientation={
                  panelSelection?.kind === 'group'
                    ? (orientation) => {
                        if (isArrayRotating || canvasMode !== 'inspect') return;
                        recordHistoryInteraction();
                        onOrientFaceGroup(panelSelection.faceId, orientation);
                      }
                    : undefined
                }
                onRemovePanels={
                  panelSelection?.kind === 'panels'
                    ? deleteSelectedPanels
                    : undefined
                }
                onMode={(mode) => {
                  if (!isArrayRotating) setCanvasMode(mode);
                }}
                onRepack={repackSelection}
                onAngle={onPreviewArrayRotation}
                onBegin={onBeginSelectedFaceRotation}
                onEnd={onEndSelectedFaceRotation}
                onApply={() => {
                  onCommitSelectedFaceRotation();
                  setCanvasMode('inspect');
                }}
                onCancel={() => {
                  onCancelSelectedFaceRotation();
                  setCanvasMode('inspect');
                }}
              />
              {repackError && (
                <div className="max-w-sm">
                  <Alert colour="yellow" iconName="alert-triangle.3">
                    <div className="flex items-center justify-between gap-2">
                      <Text>{repackError}</Text>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="shrink-0"
                        onClick={() => {
                          setRepackError(null);
                          navigateInspector('mounting');
                        }}
                      >
                        Check roof
                      </Button>
                    </div>
                  </Alert>
                </div>
              )}
            </div>
          )
        }
        map={
          <>
            <div className="absolute inset-0">
              <SolarRoofView
                apiKey={import.meta.env.VITE_GOOGLE_MAPS_KEY ?? ''}
                centre={centre}
                panels={panels}
                roofSegments={roofSegments}
                panelWidthMeters={panelWidthMeters ?? 1.045}
                panelHeightMeters={panelHeightMeters ?? 1.879}
                selectedPanelIndexes={
                  geometryMode ? includedWorkingIndexes : selectedPanelIndexes
                }
                onPanelClick={
                  panelClickDisabled || canvasMode !== 'inspect'
                    ? undefined
                    : onPanelClick
                }
                panelStringKeys={
                  geometryMode ? roofStringColouring.panelStringKeys : undefined
                }
                stringLabels={
                  geometryMode ? roofStringColouring.stringLabels : undefined
                }
                onStringLabelHover={
                  stringLabelsInteractive
                    ? (panelIndex) =>
                        setAllocationHoverIds(
                          (panelIndex == null
                            ? undefined
                            : stringAtPanel(panelIndex)?.panelIds) ?? []
                        )
                    : undefined
                }
                onStringLabelClick={
                  stringLabelsInteractive
                    ? (panelIndex) => {
                        const string = stringAtPanel(panelIndex);
                        if (!string) return;
                        setSelectedStringId(string.id);
                        setAllocationHighlightIds(string.panelIds);
                        navigateInspector('electrical');
                      }
                    : undefined
                }
                highlightedPanelIndexes={editingPanelIndexes}
                stringHighlightPanelIndexes={workingPanels.flatMap(
                  (panel, index) =>
                    panel.id && allocationHighlightIds.includes(panel.id)
                      ? [index]
                      : []
                )}
                stringHoverPanelIndexes={workingPanels.flatMap(
                  (panel, index) =>
                    panel.id && allocationHoverIds.includes(panel.id)
                      ? [index]
                      : []
                )}
                draggablePanelIndexes={
                  panelClickDisabled ||
                  canvasMode !== 'inspect' ||
                  isArrayRotating
                    ? []
                    : editingPanelIndexes
                }
                onPanelsMoved={
                  panelClickDisabled ||
                  canvasMode !== 'inspect' ||
                  isArrayRotating
                    ? undefined
                    : onPanelsMoved
                }
                snapPanelsToArray={snapPanelsToArray}
                panelGapMeters={placementRules.panelGapM}
                columnGapMeters={placementRules.columnGapM}
                rowGapMeters={placementRules.rowGapM}
                snapPanelGaps={workingFaces.map((face) =>
                  appliedArraySnap(
                    face,
                    faceOffsets[face.id] ?? 0,
                    placementRules
                  )
                )}
                snapArrayAzimuthDegrees={workingFaces.map(
                  (face) =>
                    appliedArraySnap(
                      face,
                      faceOffsets[face.id] ?? 0,
                      placementRules
                    ).arrayAzimuthDegrees
                )}
                snapRoofPlanes={workingFaces.map(
                  (face) =>
                    appliedArraySnap(
                      face,
                      faceOffsets[face.id] ?? 0,
                      placementRules
                    ).roofPlane
                )}
                faces={facePolygons}
                obstructions={geometryMode ? workingObstacles : undefined}
                obstacleEditMode={obstacleEditMode}
                onObstacleRemove={onObstacleClick}
                selectedObstructionIndex={selectedObstacleIndex}
                addObstacleMode={addingObstacle}
                onObstacleDrawn={onObstacleDrawn}
                staticFaceOutlines={staticFaceOutlines}
                packedPanels={packedPanelsForView}
                faceEditMode={isArrayRotating ? undefined : faceEditMode}
                selectedFaceIndex={selectedFaceIndexForView}
                referenceEdge={selectedFace?.roofEdgeAnchor?.endpoints}
                showAllFaceOutlines={showAllFaceOutlines}
                grabFaces={grabFacesForView && !isArrayRotating}
                moveWholeRoof={moveWholeRoofForView && !isArrayRotating}
                onRoofMoved={onRoofMoved}
                onFaceMoved={(index, delta) => {
                  if (isArrayRotating) return;
                  onFaceMoved(index, delta);
                }}
                onSelectFace={(faceArrayIndex) => {
                  if (canvasMode === 'inspect' && tool !== 'select') return;
                  if (isArrayRotating || canvasMode !== 'inspect') {
                    blockWhileRotating(
                      'Finish rotating the panel array before changing the selection.'
                    );
                    return;
                  }
                  setPanelSelection(null);
                  setSelectedFaceId(workingFaces[faceArrayIndex]?.id ?? null);
                  setSelectedObstacleIndex(null);
                }}
                // The selected roof is editable at once: vertex drags reshape
                // it, a body drag moves it. Panels or an open rotation own the
                // canvas instead.
                boundaryEditFaceIndex={
                  panelSelection || isArrayRotating || canvasMode !== 'inspect'
                    ? null
                    : boundaryEditFaceIndex
                }
                onFaceBoundaryChanged={(index, polygon) => {
                  if (
                    canvasMode !== 'inspect' ||
                    isArrayRotating ||
                    workingFaces[index]?.id !== selectedFaceId
                  )
                    return;
                  onFaceBoundaryChanged(index, polygon);
                }}
                addFaceMode={addingFace}
                addFaceTemplate={drawTemplate}
                onTemplateDrawn={onTemplateDrawn}
                addFaceAzimuthDegrees={addFaceAzimuthDegrees}
                onFaceDrawStart={onFaceDrawStart}
                fitToFacesKey={fitKey}
                onFaceDrawn={onFaceDrawn}
                fluxOverlay={fluxOverlayProp}
                showFluxOverlay={showFluxOverlay}
                rgbUnderlay={rgbUnderlayProp}
                showRgbUnderlay={showAnalysedImagery}
                underlayAtEveryZoom={solarDesignRegistersToAnalysedImagery(
                  geometry
                )}
                onOverlayImageError={onOverlayImageError}
                processingLabel={processingLabel}
                repositionMode={repositionForView}
                repositionCentre={centre}
                onRepositionChange={setRepositionPoint}
                footprints={footprintsForView}
                footprintPickMode={footprintPickActive}
                onFootprintPick={onFootprintPick}
                parcelBoundary={parcelForView}
                fill
              />
            </div>
            <div className="absolute bottom-2 left-20 right-4 flex flex-col items-start gap-2">
              {showMergedBuildingNotice && (
                <div className="pointer-events-none rounded bg-yellow-50/90 px-2 py-1">
                  <Text level="supporting">
                    <UntitledIcon
                      name="alert-triangle.3"
                      className="mr-1 inline h-3.5 w-3.5 align-text-bottom"
                    />
                    Roof spans neighbouring properties. Panels beyond the
                    property boundary start excluded.
                  </Text>
                </div>
              )}
            </div>
            {geometryMode && (
              <div className="absolute bottom-8 left-2">
                <MapLayersButton
                  showPanelsLayer={showPanelsLayer}
                  setShowPanelsLayer={setShowPanelsLayer}
                  hasFluxOverlay={!!fluxOverlayProp}
                  showFluxOverlay={showFluxOverlay}
                  setShowFluxOverlay={setShowFluxOverlay}
                  hasAnalysedImagery={!!rgbUnderlayProp}
                  showAnalysedImagery={showAnalysedImagery}
                  setShowAnalysedImagery={setShowAnalysedImagery}
                />
              </div>
            )}
            <MapAttribution
              googleData={manualRoof ? 'imagery' : 'imageryAndSolarData'}
              showsBuildingOutlines={showFootprints}
              titleBoundaryAttribution={
                parcelForView ? (geometry?.parcel?.attribution ?? null) : null
              }
            />
          </>
        }
        roof={
          <div className="space-y-4">
            {geometryMode ? (
              <>
                <ContextPanel
                  focusRequest={inspectorFocus}
                  snapToArray={snapPanelsToArray}
                  onSnapToArrayChange={setSnapPanelsToArray}
                  onFocusHandled={clearInspectorFocus}
                  tool={tool}
                  savingsRegion={savingsRegion}
                  carbonOffsetFactorKgPerMwh={
                    carbonOffsetFactorKgPerMwh ?? null
                  }
                  netMeteringAllowed={netMeteringAllowed ?? null}
                  drawTemplate={drawTemplate}
                  setDrawTemplate={setDrawTemplate}
                  onSetSelectedFacePitch={onSetSelectedFacePitch}
                  onSetSelectedFaceMounting={onSetSelectedFaceMounting}
                  onConfirmSelectedRoofPlane={onConfirmSelectedRoofPlane}
                  selectedFace={selectedFace}
                  onSetSelectedFaceAnchor={(anchor) => {
                    if (isArrayRotating) return;
                    recordHistoryInteraction();
                    onSetSelectedFaceAnchor(anchor);
                  }}
                  selectedFaceExcluded={
                    selectedFaceId != null &&
                    excludedFaceIds.includes(selectedFaceId)
                  }
                  onToggleSelectedFaceIncluded={onToggleSelectedFaceIncluded}
                  selectedObstacleIndex={selectedObstacleIndex}
                  workingPanels={workingPanels}
                  faceSummaries={faceSummaries}
                  onSelectFace={(faceId) => {
                    if (isArrayRotating) {
                      blockWhileRotating(
                        'Finish rotating the panel array before changing the selection.'
                      );
                      return;
                    }
                    setPanelSelection(null);
                    setSelectedFaceId(faceId);
                  }}
                  onSetFacePanelCount={onSetFacePanelCount}
                  workingObstacles={workingObstacles.length}
                  savings={{
                    annualKwh: generationAvailable ? annualKwh : null,
                    annualSavingsGbp: generationAvailable
                      ? (savings?.annualSavingsGbp ?? null)
                      : null,
                    paybackYears: generationAvailable
                      ? (savings?.paybackYears ?? null)
                      : null,
                  }}
                  layoutDirty={layoutDirty}
                  computedRoofPlane={(() => {
                    const analysed =
                      selectedFace && !selectedFace.isNew
                        ? geometry?.faces[selectedFace.id]
                        : undefined;
                    return analysed?.roofPitchDegrees != null &&
                      analysed.roofAzimuthDegrees != null
                      ? {
                          pitchDegrees: analysed.roofPitchDegrees,
                          azimuthDegrees: analysed.roofAzimuthDegrees,
                        }
                      : null;
                  })()}
                  geometryEditedBadge={!!geometry?.edited}
                  roofModelLabel={roofModelProviderLabel(
                    geometry?.roofModelProvider
                  )}
                  imageryQualityLabel={
                    imageryQuality ? imageryQualityPill(imageryQuality) : null
                  }
                  imageryMonth={
                    imageryDate ? formatImageryMonth(imageryDate) : null
                  }
                  isArrayRotating={isArrayRotating}
                  onIncludeSelectedFaceAnyway={() =>
                    setSelectedFaceRulesOverride(true)
                  }
                  onFollowRulesSelectedFace={() =>
                    setSelectedFaceRulesOverride(false)
                  }
                  onAutoRepack={requestAutoRepack}
                  onDeleteSelectedFace={onDeleteSelectedFace}
                  clearSelection={() => {
                    if (isArrayRotating) {
                      blockWhileRotating(
                        'Finish rotating the panel array before changing the selection.'
                      );
                      return;
                    }
                    setPanelSelection(null);
                    setSelectedFaceId(null);
                    setSelectedObstacleIndex(null);
                  }}
                  onDeleteSelectedObstacle={() => {
                    if (selectedObstacleIndex != null) {
                      onObstacleRemove(selectedObstacleIndex);
                    }
                  }}
                  isGeneratingGeometry={isGeneratingGeometry}
                  confirmReposition={confirmReposition}
                  cancelTool={() => setTool('select')}
                  parcelMismatchHint={
                    showParcelMismatchHint ? <ParcelMismatchHint /> : null
                  }
                  onResetToAuto={() => {
                    setPanelSelection(null);
                    onResetToAuto();
                  }}
                  placementRules={placementRules}
                  onPlacementRulesChange={(next) => {
                    if (isArrayRotating) return;
                    setRoofBearingRuleByFace((previous) =>
                      recordRoofBearingRules(
                        workingFaces.map((face) => face.id),
                        placementRules,
                        previous
                      )
                    );
                    setPlacementRules(next);
                  }}
                />
              </>
            ) : (
              // ── No traced geometry: Google's configurations + slider, the
              // precise-model upgrade CTA, and the wrong-roof escape
              // hatches. The pin tool works here too (via the link).
              <div className="space-y-4">
                {repositionMode ? (
                  <ContextPanel
                    tool="pin"
                    savingsRegion={savingsRegion}
                    carbonOffsetFactorKgPerMwh={null}
                    netMeteringAllowed={null}
                    drawTemplate="outline"
                    setDrawTemplate={() => undefined}
                    onSetSelectedFacePitch={() => undefined}
                    selectedFace={undefined}
                    selectedObstacleIndex={null}
                    workingPanels={[]}
                    workingObstacles={0}
                    savings={{
                      annualKwh,
                      annualSavingsGbp: savings?.annualSavingsGbp ?? null,
                      paybackYears: savings?.paybackYears ?? null,
                    }}
                    layoutDirty={false}
                    geometryEditedBadge={false}
                    isArrayRotating={false}
                    faceSummaries={[]}
                    onSelectFace={() => undefined}
                    onSetFacePanelCount={() => undefined}
                    onDeleteSelectedFace={() => undefined}
                    clearSelection={() => undefined}
                    onDeleteSelectedObstacle={() => undefined}
                    isGeneratingGeometry={isGeneratingGeometry}
                    confirmReposition={confirmReposition}
                    cancelTool={() => setTool('select')}
                    parcelMismatchHint={
                      showParcelMismatchHint ? <ParcelMismatchHint /> : null
                    }
                    onResetToAuto={onResetToAuto}
                  />
                ) : (
                  <SuggestedLayoutControls
                    panelsCount={panelsCount}
                    availablePanels={panels.length}
                    configurationCount={configurations.length}
                    configurationIndex={sliderIndex}
                    customLayout={configIndex == null}
                    isGenerating={isGeneratingGeometry}
                    onSelectConfiguration={onSelectConfig}
                    onGenerate={() => generateSolarGeometry({ projectId })}
                    onReposition={() => setTool('pin')}
                    onDraw={startManualDraw}
                  />
                )}
              </div>
            )}

            {isGenerateError && (
              <Alert colour="red" iconName="alert-triangle.3">
                <div className="space-y-2">
                  <Text>{props.generationError}</Text>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={isGeneratingGeometry}
                    onClick={() => generateSolarGeometry({ projectId })}
                  >
                    Retry analysis
                  </Button>
                </div>
              </Alert>
            )}
            {props.designerStateRefreshFailed && (
              <Alert colour="yellow" iconName="alert-triangle.3">
                The latest saved design could not be checked. Your edits are
                still here; save them before reloading the page.
              </Alert>
            )}
            {generatedWithoutGeometry && !geometry && !roofRecoveryMessage && (
              <Alert colour="yellow" iconName="alert-triangle.3">
                A precise roof model couldn&apos;t be traced for this property.
                The satellite-derived layout is still usable.
              </Alert>
            )}
            {props.staleDraft && !needsRecovery && (
              <Alert colour="yellow" iconName="alert-triangle.3">
                The previous draft belongs to an older roof analysis or location
                and has been kept. Automatic saving is paused. Choose Replace
                previous draft to review replacing it with this design.
              </Alert>
            )}
          </div>
        }
        equipment={
          <div className="space-y-4">
            {inverterMatches.isError && (
              <Alert colour="red" iconName="alert-triangle.3">
                <div className="space-y-2">
                  <Text>Inverter recommendations could not be loaded.</Text>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      void inverterMatches.refetch();
                    }}
                  >
                    Retry recommendations
                  </Button>
                </div>
              </Alert>
            )}
            {productRecommendationWarning && (
              <Alert colour="yellow" iconName="alert-triangle.3">
                {productRecommendationWarning}
              </Alert>
            )}
            {allocationError && (
              <Alert colour="yellow">
                <Text>{allocationError}</Text>
              </Alert>
            )}
            {!selectedPanelAppliance && (
              <Alert colour="yellow" iconName="alert-triangle.3">
                No priced panel matches this layout. Check your preferred panel
                and generate a new roof model before creating an estimate.
              </Alert>
            )}
            <div>
              <ProductsPanel
                electricalTopology={draftTopology}
                electricalPrices={electricalPrices}
                electricalAllocationActive={!!draftTopology}
                onOpenElectrical={() => navigateInspector('electrical')}
                focusRequest={inspectorFocus}
                onFocusHandled={clearInspectorFocus}
                recommendedPanelAppliances={recommendedPanelAppliances}
                recommendedInverterAppliances={
                  selectedInverterAppliance &&
                  !inverterOptions.some(
                    (r) =>
                      r.appliance.id === selectedInverterAppliance.appliance.id
                  )
                    ? [...inverterOptions, selectedInverterAppliance]
                    : inverterOptions
                }
                recommendedBatteryAppliances={recommendedBatteryAppliances}
                selectedPanelApplianceId={selectedPanelAppliance?.appliance.id}
                selectedInverterApplianceId={
                  selectedInverterAppliance?.appliance.id
                }
                selectedBatteryApplianceId={
                  selectedBatteryAppliance?.appliance.id
                }
                batteryQuantity={batteryQuantity}
                onBatteryQuantityChange={setBatteryQuantity}
                onSelectPanelAppliance={(id) => {
                  if (draftTopology) {
                    setAllocationError(
                      'This design has pinned panel selections. Create a new design for a different panel model, or use a targeted equipment-selection revision.'
                    );
                    return;
                  }
                  setPickedPanelApplianceId(id);
                }}
                onSelectInverterAppliance={(id) => {
                  setPickedInverterApplianceId(id);
                  if (
                    inverterOptions.find((r) => r.appliance.id === id)
                      ?.appliance.productSpec.pvConversion
                  ) {
                    setIncludeBattery(true);
                    setPickedBatteryApplianceId(id ?? undefined);
                  }
                }}
                excludedInverterAppliances={
                  inverterMatches.excludedInverterAppliances
                }
                onSelectBatteryAppliance={(id) => {
                  setPickedBatteryApplianceId(id);
                  if (
                    selectedInverterAppliance?.appliance.productSpec
                      .pvConversion &&
                    id !== selectedInverterAppliance.appliance.id
                  )
                    setPickedInverterApplianceId(undefined);
                }}
                includeBattery={includeBattery}
                setIncludeBattery={(included) => {
                  setIncludeBattery(included);
                  if (
                    !included &&
                    selectedInverterAppliance?.appliance.productSpec
                      .pvConversion
                  ) {
                    setPickedInverterApplianceId(undefined);
                    setPickedBatteryApplianceId(undefined);
                  }
                }}
                panelsCount={quotedModules.panelsCount}
                arrayKwp={arrayKwp}
                projectId={projectId}
                populatedRoofArraysCount={
                  geometryMode
                    ? faceSummaries.filter((face) => face.panelsIncluded > 0)
                        .length
                    : new Set(
                        panels
                          .filter((_, index) => selectedIndexSet.has(index))
                          .map((panel) => panel.segmentIndex)
                      ).size
                }
                showG99Hint={account.region === AccountRegions.UK}
                inverterPhases={inverterPhases}
                allocatedInverterAcKw={allocatedInverterAcKw}
              />
            </div>
            {geometryMode &&
              recommendedPanelAppliances.length < allPanelAppliances.length && (
                <Text level="supporting">
                  Only panels matching this layout's dimensions and capacity are
                  shown. Generate a new layout after changing your preferred
                  panel.
                </Text>
              )}
          </div>
        }
        electrical={
          <ElectricalTab
            geometryMode={geometryMode}
            onDrawRoof={
              canvasDisabledReason
                ? undefined
                : () => {
                    clearInspectorFocus();
                    if (geometryMode) setTool('draw');
                    else startManualDraw();
                  }
            }
            onChooseInverter={() => navigateInspector('equipment')}
            layoutChangeDisabledReason={electricalLayoutChangeDisabledReason}
            onConfigure={
              geometryMode &&
              selectedInverterAppliance?.appliance.productSpec.category !==
                'BATTERY' &&
              selectedInverterAppliance?.appliance.productSpec
                .inverterTopology !== 'MICRO'
                ? () => {
                    if (rotationPreviewRef.current) return;
                    const upgraded = upgradeSolarDesignElectrical(
                      draftState,
                      electricalNamespace
                    );
                    if (upgraded.error) {
                      setAllocationError(upgraded.error);
                      return;
                    }
                    electricalAdoptedRef.current = true;
                    restoreLayout(upgraded.layout);
                    setElectrical(
                      allocatedSolarElectrical(upgraded.electrical) ?? undefined
                    );
                    // The same click strings the design.
                    setAutoStringPending(true);
                  }
                : undefined
            }
            error={allocationError}
            allocation={
              draftTopology ? (
                <>
                  <ElectricalAllocationPanel
                    layoutChangeDisabledReason={
                      electricalLayoutChangeDisabledReason
                    }
                    topology={draftTopology}
                    panels={draftState.layout.workingPanels}
                    faces={allocationFaces}
                    excludedFaceIds={draftState.layout.excludedFaceIds}
                    selectedPanelIds={editingPanelIndexes.flatMap((index) =>
                      workingPanels[index]?.id ? [workingPanels[index].id] : []
                    )}
                    appliances={[
                      ...inverterOptions,
                      ...inverterMatches.excludedInverterAppliances.filter(
                        (item) => item.blockingReason === null
                      ),
                    ]}
                    panelAppliances={recommendedPanelAppliances}
                    siteMinTemperatureC={siteMinTemperatureC}
                    region={savingsRegion}
                    battery={includeBattery ? selectedBatteryAppliance : null}
                    autoStringOnMount={autoStringPending}
                    onAutoStringHandled={() => setAutoStringPending(false)}
                    onHighlightPanels={setAllocationHighlightIds}
                    onHoverPanels={setAllocationHoverIds}
                    colourKeys={roofStringColouring.colourKeys}
                    selectedStringId={selectedStringId}
                    onSelectedStringIdChange={setSelectedStringId}
                    onChange={(change) => {
                      if (
                        change.type === 'revisePanelGroup' &&
                        rotationPreviewRef.current
                      )
                        return;
                      const result = applySolarElectricalDesignChange(
                        electricalDraftRef.current,
                        change
                      );
                      if (result.error) {
                        setAllocationError(result.error);
                        return;
                      }
                      electricalDraftRef.current = result.state;
                      setAllocationError(null);
                      setElectrical(
                        allocatedSolarElectrical(result.state.electrical) ??
                          undefined
                      );
                      if (change.type === 'revisePanelGroup')
                        restoreLayout(result.state.layout);
                    }}
                  />
                </>
              ) : undefined
            }
          />
        }
        performance={
          <div className="space-y-3">
            {savingsRegion === 'us' && (
              <div className="space-y-2">
                <H4>PVWatts weather estimate</H4>
                <Text level="supporting">{weatherStatus}</Text>
                <Tooltip
                  tooltipContent={calculateTooltip ?? ''}
                  enabled={!!calculateTooltip}
                >
                  <Button
                    size="sm"
                    variant="secondary"
                    isProcessing={calculatePerformance.isPending}
                    aria-disabled={
                      !!calculateTooltip ||
                      isRecovering ||
                      calculatePerformance.isPending
                    }
                    onClick={() => {
                      if (
                        calculateTooltip ||
                        isRecovering ||
                        calculatePerformance.isPending
                      )
                        return;
                      void calculateWeather();
                    }}
                  >
                    Calculate
                  </Button>
                </Tooltip>
                {calculateDisabledReason && (
                  <Text level="supporting">{calculateDisabledReason}</Text>
                )}
                {weather?.arrays
                  .flatMap((array) => array.response.warnings)
                  .map((warning, index) => (
                    <Text key={index} level="supporting">
                      {warning}
                    </Text>
                  ))}
              </div>
            )}
            <DesignerSummary
              panelsCount={quotedModules.panelsCount}
              arrayKwp={arrayKwp}
              annualKwh={generationAvailable ? annualKwh : null}
              annualSavings={
                generationAvailable ? (savings?.annualSavingsGbp ?? null) : null
              }
              equipmentSubtotal={systemCostGbp}
              currencySymbol={currencySymbol}
              generationBasis={
                weatherGeneration
                  ? 'PVWatts weather estimate'
                  : currentGenerationBasis
              }
              generationMessage={generationMessage}
            />
            <HouseholdElectricitySection
              projectId={projectId}
              addressId={props.siteAddressId}
              household={household}
              showOccupancy={account.region === AccountRegions.UK}
              onHouseholdChange={setHousehold}
            />
            {savingsRegion === 'us' && (
              <UsNetMeteringScenario
                policy={monthlyPolicy}
                onPolicyChange={setUsNetMeteringPolicy}
                monthlyAcKwh={monthlyScenarioGeneration}
                weatherSource={weatherGeneration !== undefined}
                annualDemandKwh={
                  household?.annualElectricityConsumptionKwh ?? null
                }
                retailCentsPerKwh={savingsRates.retailTariffMinorPerKwh}
                exportCentsPerKwh={savingsRates.exportTariffMinorPerKwh}
                selfUseFraction={assumedSelfUseFraction}
                site={centre}
              />
            )}
          </div>
        }
        summary={
          <DesignerSummary
            onEquipmentClick={() => navigateInspector('equipment')}
            compact
            panelsCount={quotedModules.panelsCount}
            arrayKwp={arrayKwp}
            annualKwh={generationAvailable ? annualKwh : null}
            annualSavings={null}
            equipmentSubtotal={systemCostGbp}
            currencySymbol={currencySymbol}
            generationBasis={currentGenerationBasis}
            inverterAcKw={selectedInverterAcKw ?? null}
          />
        }
        notices={
          (needsRecovery ||
            noticeError ||
            createError ||
            repackNotice != null) && (
            <>
              {needsRecovery && (
                <NoticeLine
                  actions={
                    <>
                      {recoveryBaseline.known && (
                        <Button
                          size="sm"
                          variant="primary"
                          disabled={isBusy || !props.readRecoverySource}
                          isProcessing={isRecovering}
                          onClick={() => void keepMyLayout()}
                        >
                          Keep my layout
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="quaternary"
                        onClick={() => setRecoveryDetailsOpen(true)}
                      >
                        Details
                      </Button>
                    </>
                  }
                >
                  {recoveryLine}
                </NoticeLine>
              )}
              {createError && (
                <NoticeLine
                  onDismiss={resetCreateEstimate}
                  actions={
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={!!createDisabledReason || isBusy}
                      onClick={() => void onCreateEstimate()}
                    >
                      Retry
                    </Button>
                  }
                >
                  {extractErrorMessage(createError).message}
                </NoticeLine>
              )}
              {noticeError && (
                <NoticeLine onDismiss={() => setActionError(null)}>
                  {noticeError}
                </NoticeLine>
              )}
              {repackNotice != null && (
                <NoticeLine
                  colour="blue"
                  onDismiss={() => setRepackNotice(null)}
                  actions={
                    <Button
                      size="sm"
                      variant="quaternary"
                      onClick={() => designHistory.undo()}
                    >
                      Undo
                    </Button>
                  }
                >
                  {repackNotice} roof{repackNotice === 1 ? '' : 's'} repacked
                </NoticeLine>
              )}
            </>
          )
        }
      />
      <Modal
        isOpen={recoveryDetailsOpen}
        onClose={() => setRecoveryDetailsOpen(false)}
        title="Roof analysis changed"
        disableBackdropClick={false}
      >
        <Modal.Body className="space-y-3">
          {recoveryBaseline.known ? (
            <>
              <Text>
                The roof analysis was refreshed since this layout was saved.
                Your layout is still here and automatic saving is paused.
              </Text>
              <Text>
                Choose Keep my layout to save it against the refreshed analysis.
              </Text>
            </>
          ) : (
            <>
              <Text>
                The site or panel size no longer matches the roof analysis this
                layout was drawn on. Your layout is still here and automatic
                saving is paused.
              </Text>
              <Text>{recoveryBaseline.reason}</Text>
            </>
          )}
        </Modal.Body>
      </Modal>
      <EditSolarDesignDetailsModal
        isOpen={editingDetails}
        name={props.draftName}
        description={props.draftDescription}
        unavailableReason={detailsDisabledReason}
        isSaving={saveDraft.isPending}
        onClose={() => setEditingDetails(false)}
        onSave={saveDetails}
      />
      <Modal
        isOpen={deletingDesign}
        onClose={() => setDeletingDesign(false)}
        title="Delete this design?"
      >
        <Modal.Body className="space-y-3">
          <Text>
            {props.draftName} will be deleted permanently, with its roof edits
            and panel layout. This cannot be undone.
          </Text>
          {deleteDesign.isError && (
            <Alert colour="red">
              <Text>
                The design could not be deleted. It may already have been used
                on an estimate.
              </Text>
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="secondary"
              onClick={() => setDeletingDesign(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isProcessing={deleteDesign.isPending}
              disabled={deleteDesign.isPending}
              onClick={() => {
                if (!props.designId) return;
                deleteDesign.mutate(
                  { designId: props.designId },
                  { onSuccess: () => leaveDesigner() }
                );
              }}
            >
              Delete design
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
      <DuplicateSolarDesignModal
        isOpen={duplicatingDesign}
        projectId={projectId}
        projectPublicId={projectPublicId}
        draftId={props.designId}
        name={props.draftName}
        description={props.draftDescription}
        source={solarPotential}
        unavailableReason={detailsDisabledReason}
        onClose={() => setDuplicatingDesign(false)}
      />
      <Modal
        isOpen={confirmingReplacement}
        onClose={() => setConfirmingReplacement(false)}
        title="Replace the previous draft?"
      >
        <Modal.Body>
          <Text>
            This will replace the saved design from the previous roof analysis
            or location with the design shown here. Its previous edits will be
            lost.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="secondary"
              onClick={() => setConfirmingReplacement(false)}
            >
              Cancel
            </Button>
            <Button
              isProcessing={saveDraft.isPending}
              disabled={saveDraft.isPending}
              onClick={() => {
                void persistDesign(true)
                  .then(() => setConfirmingReplacement(false))
                  .catch(() => undefined);
              }}
            >
              Replace draft
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
      <Modal
        isOpen={confirmingCancel}
        onClose={() => setConfirmingCancel(false)}
        title="Leave without saving?"
      >
        <Modal.Body>
          <Text>
            Your roof edits and panel changes have not been saved. Leaving now
            discards them and the design goes back to the last saved layout.
          </Text>
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Actions>
            <Button
              variant="secondary"
              colour="negative"
              onClick={() => {
                setConfirmingCancel(false);
                // Explicit discard ends the local preview without committing it.
                restoreDesign(draftState, 'remote');
                designHistory.markSaved(draftState);
                // Let the unsaved-navigation guard unregister before leaving.
                setTimeout(leaveDesigner, 0);
              }}
            >
              Discard and leave
            </Button>
            <Button onClick={() => setConfirmingCancel(false)}>
              Keep editing
            </Button>
          </Modal.Footer.Actions>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SolarDesignerContent;
